import { useState, useMemo, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { usePullToRefresh } from "../../utils/pullToRefresh/usePullToRefresh";
import { PullToRefreshIndicator } from "../../utils/pullToRefresh/PullToRefreshIndicator";
import { useBranch } from "../../context/BranchContext";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar, { DATE_PRESETS_DESKTOP, DATE_PRESETS_MOBILE } from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
    fetchChannelPerformanceRequest,
    fetchCohortRetentionRequest,
    fetchDrilldownCustomersRequest,
    fetchDrilldownStatsRequest,
    fetchSegmentSpendRequest,
    fetchCustomerDirectoryRequest,
    fetchCustomerSegmentsRequest,
    fetchCustomerStampKpisRequest,
    fetchCustomerDetailsRequest,
    clearCustomerDetails,
    fetchAcquisitionSourceRequest,
} from "../../redux/reports/customersReport/customersReportActions";
// The directory export is a one-shot fetch whose result goes straight into a
// file, so it calls the endpoint directly rather than through the saga — there
// is no store state for it to own, and routing it through the table's slice
// would overwrite the page the user is looking at.
import { getCustomerDetailsApi, getCustomerDirectoryApi, getCustomerSegmentBreakdownApi, refreshSegmentBreakdownApi } from "../../redux/reports/customersReport/customersReportApi";
import type {
    OrderLogSortKey,
    CustomerDirectoryRequest,
    CustomerDirectoryResponse,
    CustomerDirectoryRow,
    CustomerDirectorySortKey,
    DrilldownCustomersResponse,
    DrilldownKey,
    DrilldownStatsResponse,
    TopAtRiskCustomer,
} from "../../interface/customersReportInterface";
import { resolveTokenRange, exportPeriodFor } from "../../utils/reportPeriod";
import { formatMoneyForExport } from "../../utils/currency";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { currentStoreDate } from "../../utils/dateTimeUtils";
import { isTodayRange } from "../../services/reportsApi";
import { useSwipeBack } from "../../hooks/useSwipeBack";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import Arrow from "../Arrow";
import { T, useIsDesktop, Bone } from "./reportTokens";
import { LiveCustomerProfile } from "./customersReportProfile";
import { Icon, InfoTip, SectionCard, SegmentBadge, Avatar, BarRow, useMoney, PageSizeSelect, PaginationBar, TapTip, CustomerSearchBox } from "./customersReportShared";
import { CustomerProfile, ExportConsentDialog, ExportFlowSpec, useContactConsent, type ContactConsent } from "./customersReportViews";
import { useSegmentSync, formatFreshness, exactTimestamp } from "./customersSegmentSync";
import {
    SEGMENTS, SEGMENT_INFO, cohortColor, COMPLAINTS, CANCELLATIONS,
    acquisitionByKey, CUST_CONFIG, infoCopy, segmentColor, channelIcon,
    getCustomerById, CustomerRecord,
} from "./customersReportData";
import {
    DEMO_CHANNELS, DEMO_SEGMENTS, DEMO_SEGMENT_SPEND, DEMO_STAMP_KPIS,
    buildDemoAcquisition,
    buildDemoCohorts, buildDemoDirectory,
    buildDemoDrilldownCustomers, buildDemoDrilldownStats,
} from "./customersReportDemoData";
import "./reports.css";
import "./CustomersReport.scss";

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — CUSTOMERS REPORT (CUST-* v2.4, P0 + P1)
   Single responsive component (desktop ≥900px + mobile). Live New/Returning/
   segment counts are merged over the spec datasets where available; every other
   metric is fed from customersReportData.ts (future ETL phases, no live API yet).
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── Navigation model: dashboard → drill → profile ───────────────────────── */
/**
 * A drill-down TARGET, not its data. The header and the table each fetch for
 * themselves from endpoints that share one definition of the customer set, so
 * CUST-6.1's "title — 438 AND the table filtered to…" cannot disagree.
 *
 * Every drill is live now. The `mock*` escape hatch that Acquisition Source
 * used before CUST-19.2 had an endpoint is gone — demo mode reaches its rows
 * the same way every other drill does.
 */
interface DrillSpec {
    key: string;
    drill: DrilldownKey;
    /** Required when `drill` is `segment`. */
    segmentKey?: string;
    /** Required when `drill` is `acquisition`. */
    sourceKey?: string;
    badge?: { label: string; color: string };
    /**
     * Header figures supplied by the CARD that opened the drill, used instead
     * of fetching them. Acquisition Source is the one case: its card already
     * holds the three numbers CUST-19.1 asks for (new, 30-day repeat, avg CLV)
     * from a live endpoint, and they are the numbers the operator just clicked
     * — re-deriving them from the generic drill header would show a different
     * set and invite the question of which is right.
     */
    headerStats?: { label: string; value: string }[];
}

/**
 * Refresh Segments: `refresh-segment-breakdown` returns once ClickHouse ACCEPTS the
 * rebuild (~40 ms), not once `fact_customers` is rebuilt (~1 s). The screen waits
 * for the Segment Breakdown's `refreshedAt` to move before reloading every card,
 * polling at this interval for at most this long.
 */
const REBUILD_POLL_MS = 1000;
const REBUILD_WAIT_MS = 15000;

/** CUST-6.2 — "load 50, then Load More in batches of 50". */
const DRILL_PAGE_SIZE = 50;
/** Shimmer rows a customer table (a drill or the Customer Directory) shows while its page loads (QA #15). */
const DRILL_SKELETON_ROWS = 8;

/** CUST-16 — a customer's order log pages independently of the tables. */
const ORDER_LOG_PAGE_SIZE = 25;
/** CUST-7.2 — the Visit Timeline loads 10 visits, then 10 more per "Show earlier visits". */
const VISIT_TIMELINE_PAGE_SIZE = 10;
/** Mirrors the backend's `MAX_ORDER_LOG_PAGE_SIZE` - the export pages at this. */
const ORDER_LOG_EXPORT_PAGE_SIZE = 500;
type View =
    | { kind: "dashboard" }
    | { kind: "drill"; drill: DrillSpec; from: View }
    | { kind: "profile"; customerId: string; from: View };

/* ─── Top-bar breadcrumb (CUST-6 / CUST-7) ────────────────────────────────
   A drill and a profile are full-screen views reached from the dashboard, but
   the top bar used to read "Reports › Customers" in all three — so entering a
   drill looked like nothing had happened, and there was no on-screen clue
   where the Back button would take you. The crumb now names the view. */

/** The three stamp drills. `segment` is absent on purpose — it covers both RFM
 *  segments and acquisition sources, and for those the spec that opened the
 *  drill already carries the exact name the user clicked. */
const DRILL_CRUMB: Record<string, string> = {
    new: "New Customers",
    returning: "Returning Customers",
    at_risk: "At Risk",
};

interface Crumb {
    /** `›`-separated; TopBar splits it and renders `title` as the bold last part. */
    breadcrumbs: string;
    title: string;
}

function crumbFor(view: View, profileName: string | null): Crumb {
    if (view.kind === "drill") {
        return {
            breadcrumbs: "Reports › Customers",
            title: DRILL_CRUMB[view.drill.drill] ?? view.drill.badge?.label ?? "Drill-down",
        };
    }
    if (view.kind === "profile") {
        // Live name first, then a generic label while the profile is still
        // loading — so the crumb never shows a stale name from the previously
        // opened customer. The mock lookup is only consulted when sample data is
        // switched on; with it off, no invented name can reach the top bar.
        const mockName = enablePreviewWithSampleDataMode
            ? (getCustomerById(view.customerId)?.name ?? null)
            : null;
        return {
            breadcrumbs: "Reports › Customers",
            title: profileName ?? mockName ?? "Customer",
        };
    }
    return { breadcrumbs: "Reports", title: "Customers" };
}

/* ═══════════════════════════════════════════════════════════════════════════
   DEMO MODE — review the UI before the endpoints are deployed
   ---------------------------------------------------------------------------
   Every card on this screen is live now, so an undeployed backend leaves the
   whole dashboard on error strips — and the drill-downs unreachable, because
   `drillable` is gated on a KPI actually having a value. Demo mode feeds each
   hook an API-shaped response built from the mock datasets instead.

   Off by default and never automatic: it is switched on explicitly and a banner
   stays on screen throughout, so mock numbers cannot be mistaken for live ones.
   It is NOT a fallback — a failing endpoint still shows its error, because
   silently swapping in fake data on a network failure is how wrong numbers
   reach a customer.
   ═══════════════════════════════════════════════════════════════════════════ */
const DEMO_MODE_KEY = "customersReport.demoMode";

/**
 * Master switch for "Preview with sample data".
 *
 * `true` shows the toggle and lets the screen render invented figures; `false`
 * hides it and the report is live-only.
 *
 * **Off means off for everyone, including anyone who already turned it on.**
 * The setting is persisted in `localStorage` and can also be set by a `?demo=1`
 * link, so a browser that enabled it once would otherwise keep showing sample
 * data for ever — with the toggle now hidden, and no way back. `readDemoMode`
 * therefore clears the stored flag and ignores the URL parameter when this is
 * false, rather than merely hiding the button.
 */
export const enablePreviewWithSampleDataMode: boolean = false;

const readDemoMode = (): boolean => {
    if (!enablePreviewWithSampleDataMode) {
        // Actively clear it: a browser that enabled demo mode before the switch
        // was turned off must not stay stuck on sample data.
        try { window.localStorage.removeItem(DEMO_MODE_KEY); } catch { /* ignore */ }
        return false;
    }
    try {
        // `?demo=1` wins for a one-off look and persists, so a QA link works
        // without walking someone through the toggle.
        const param = new URLSearchParams(window.location.search).get("demo");
        if (param === "1" || param === "0") {
            const on = param === "1";
            window.localStorage.setItem(DEMO_MODE_KEY, String(on));
            return on;
        }
        return window.localStorage.getItem(DEMO_MODE_KEY) === "true";
    } catch {
        // Private mode / blocked storage — demo mode simply stays off.
        return false;
    }
};

function useDemoMode(): [boolean, (on: boolean) => void] {
    const [demo, setDemo] = useState(readDemoMode);
    const set = useCallback((on: boolean) => {
        // Guarded as well as hidden, so a stray call cannot switch the whole
        // screen to invented numbers while the switch is off.
        if (!enablePreviewWithSampleDataMode) return;
        setDemo(on);
        try { window.localStorage.setItem(DEMO_MODE_KEY, String(on)); } catch { /* ignore */ }
    }, []);
    return [enablePreviewWithSampleDataMode && demo, set];
}

/** The shape every section hook returns, so demo data can stand in for any of them. */
const demoResult = <T,>(data: T) => ({ data, loading: false, error: null as string | null, refetch: () => {} });

/* ─── The period a request sends ─────────────────────────────────────────────
   Today sends NO dates: that is how the backend knows to read the live tables
   (`v1_live_fact_orders`) instead of the historical ones. "Today" is judged
   from the RESOLVED range, as the live Sales panels do (ZAR-1762), so today
   picked in the calendar counts the same as the Today quick-select.
   `startDate` / `endDate` stay the real dates either way - the guards, the
   directory's page reset and the demo data all still need them. */
function useRequestPeriod(datePeriod: string) {
    return useMemo(() => {
        const range = resolveTokenRange(datePeriod);
        return {
            startDate: range?.startDate ?? "",
            endDate: range?.endDate ?? "",
            live: !!range && isTodayRange(range.startDate, range.endDate, currentStoreDate()),
        };
    }, [datePeriod]);
}

/*
 * No `comparisonBasis` on this report's date control any more (QA #10). The stamp KPIs
 * now send the comparison window themselves - `comparisonQueryParams` in the saga, the
 * same shape rule as Snapshot and Sales (a Monday vs the previous Monday, Today vs the
 * same weekday up to the same time) - so the control's default label names exactly the
 * window the deltas use. DPFIX-13's 'preceding' basis described the backend's old
 * day-over-day rule, which is what QA reported.
 */

/** What actually travels: both dates, or none for Today. */
const sentDates = (live: boolean, startDate: string, endDate: string) =>
    live ? {} : { startDate, endDate };

/* ─── Stamp KPIs (CUST-1 / 2 / 4) — /api/clickhouse/customer-reports/stamp-kpis ─── */
function useCustomerStampKpis(locationId: string, datePeriod: string, demo = false) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.stampKpis);
    const loading = useAppSelector((s) => s.customersReport.stampKpisLoading);
    const error = useAppSelector((s) => s.customersReport.stampKpisError);
    const { startDate, endDate, live } = useRequestPeriod(datePeriod);

    const refetch = useCallback(() => {
        if (demo || !locationId || !startDate || !endDate) return;
        dispatch(fetchCustomerStampKpisRequest({ locationId, ...sentDates(live, startDate, endDate) }));
    }, [demo, dispatch, locationId, live, startDate, endDate]);

    useEffect(() => { refetch(); }, [refetch]);

    if (demo) return demoResult(DEMO_STAMP_KPIS);

    // Nothing fetched yet and nothing failed counts as loading, so the cards
    // shimmer on first paint instead of flashing "—".
    return { data, loading: loading || (!data && !error && !!locationId), error, refetch };
}

/* ─── Segment Breakdown (CUST-3.2 / 3.3) — /segment-breakdown-kpi ─────────── */
function useCustomerSegments(locationId: string, demo = false) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.segments);
    const loading = useAppSelector((s) => s.customersReport.segmentsLoading);
    const error = useAppSelector((s) => s.customersReport.segmentsError);

    // No dates: segments are a snapshot of the customer base, and the date
    // filter does not affect RFM scoring (CUST-10.1).
    const refetch = useCallback(() => {
        if (demo || !locationId) return;
        dispatch(fetchCustomerSegmentsRequest({ locationId }));
    }, [demo, dispatch, locationId]);

    useEffect(() => { refetch(); }, [refetch]);

    if (demo) return demoResult(DEMO_SEGMENTS);

    return { data, loading: loading || (!data && !error && !!locationId), error, refetch };
}

/* ─── Channel Performance (CUST-13.1) — /channel-performance-kpi ──────────
   Takes the period, unlike the Segment Breakdown: channel mix is a slice of
   the selected dates, not a snapshot of the base. */
function useChannelPerformance(locationId: string, datePeriod: string, demo = false) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.channels);
    const loading = useAppSelector((s) => s.customersReport.channelsLoading);
    const error = useAppSelector((s) => s.customersReport.channelsError);
    const { startDate, endDate, live } = useRequestPeriod(datePeriod);

    const refetch = useCallback(() => {
        if (demo || !locationId || !startDate || !endDate) return;
        dispatch(fetchChannelPerformanceRequest({ locationId, ...sentDates(live, startDate, endDate) }));
    }, [demo, dispatch, locationId, live, startDate, endDate]);

    useEffect(() => { refetch(); }, [refetch]);

    if (demo) return demoResult(DEMO_CHANNELS);

    return { data, loading: loading || (!data && !error && !!locationId), error, refetch };
}

/* ─── Avg Spend by Segment (CUST-12.1) — /avg-spend-by-segment-kpi ────────
   Period-scoped, unlike the Segment Breakdown: CUST-10.1 lists "segment AOV"
   among what the date filter affects. The backend also returns the prior
   period's average, so the delta needs no second request. */
function useSegmentSpend(locationId: string, datePeriod: string, demo = false) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.segmentSpend);
    const loading = useAppSelector((s) => s.customersReport.segmentSpendLoading);
    const error = useAppSelector((s) => s.customersReport.segmentSpendError);
    const { startDate, endDate, live } = useRequestPeriod(datePeriod);

    const refetch = useCallback(() => {
        if (demo || !locationId || !startDate || !endDate) return;
        dispatch(fetchSegmentSpendRequest({ locationId, ...sentDates(live, startDate, endDate) }));
    }, [demo, dispatch, locationId, live, startDate, endDate]);

    useEffect(() => { refetch(); }, [refetch]);

    if (demo) return demoResult(DEMO_SEGMENT_SPEND);

    return { data, loading: loading || (!data && !error && !!locationId), error, refetch };
}

/* ─── Cohort Retention (CUST-11.1) — /cohort-retention-kpi ────────────────
   Only the period END means anything: the window is always the 12 months
   ending there (CUST-10.1 lists "cohort window" among what the date filter
   affects), so a start date cannot narrow it. Every Customers Report endpoint
   takes both dates or neither though, so `endDate` is sent as both rather than
   sending the period's real start — which would read as if it mattered. Today
   sends neither, like every other section. */
function useCohortRetention(locationId: string, datePeriod: string, demo = false) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.cohorts);
    const loading = useAppSelector((s) => s.customersReport.cohortsLoading);
    const error = useAppSelector((s) => s.customersReport.cohortsError);
    const { endDate, live } = useRequestPeriod(datePeriod);

    const refetch = useCallback(() => {
        if (demo || !locationId || !endDate) return;
        dispatch(fetchCohortRetentionRequest({ locationId, ...sentDates(live, endDate, endDate) }));
    }, [demo, dispatch, locationId, live, endDate]);

    useEffect(() => { refetch(); }, [refetch]);

    if (demo) return demoResult(buildDemoCohorts(endDate || "2026-09-21"));

    return { data, loading: loading || (!data && !error && !!locationId), error, refetch };
}

/* ─── Acquisition Source (CUST-19.1) — /acquisition-source-kpi ───────────
   Period-scoped like Channel Performance. Note each row's `avgClv` is
   HISTORICAL, not period — see the endpoint's own docs. */
function useAcquisitionSources(locationId: string, datePeriod: string, demo = false) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.acquisition);
    const loading = useAppSelector((s) => s.customersReport.acquisitionLoading);
    const error = useAppSelector((s) => s.customersReport.acquisitionError);
    const { startDate, endDate, live } = useRequestPeriod(datePeriod);

    const refetch = useCallback(() => {
        if (demo || !locationId || !startDate || !endDate) return;
        dispatch(fetchAcquisitionSourceRequest({ locationId, ...sentDates(live, startDate, endDate) }));
    }, [demo, dispatch, locationId, live, startDate, endDate]);

    useEffect(() => { refetch(); }, [refetch]);

    if (demo) return demoResult(buildDemoAcquisition());

    return { data, loading: loading || (!data && !error && !!locationId), error, refetch };
}

/* ─── One customer (CUST-7 / 16 / 17) — /customer-details-drilldowns ──────
   The row click, and it is the SAME call from the Customer Directory and from
   any drill table, because both tables are now `customer-directory-kpi` and
   both return the `customerId` this takes. */
function useCustomerDetails(
    customerId: string | null,
    scope: { locationId: string; startDate: string; endDate: string; live: boolean },
    demo: boolean,
) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.customersReport.details);
    const loading = useAppSelector((s) => s.customersReport.detailsLoading);
    const error = useAppSelector((s) => s.customersReport.detailsError);
    const [page, setPage] = useState(1);
    // CUST-7.3 sortable order log (QA #20) - sorted server-side across the whole
    // log, so it is a request parameter, not a client-side sort of one page.
    const [sort, setSort] = useState<OrderLogSort>(DEFAULT_ORDER_LOG_SORT);
    // QA #57 - rows per page and a search, both request parameters: the API pages
    // and filters the whole log, so neither is a client-side trim of one page.
    // `searchInput` is what is typed; `search` is what is sent, 300ms later - the
    // Directory's own debounce, so a keystroke is not a request.
    const [pageSize, setPageSize] = useState(ORDER_LOG_PAGE_SIZE);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    useEffect(() => {
        const id = window.setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => window.clearTimeout(id);
    }, [searchInput]);

    // A new customer starts at page 1 of THEIR log, in the default order, not
    // wherever the last one was left - otherwise opening a 2-order customer
    // after paging deep into a 90-order one shows an empty page. Their search
    // and page size reset too: another customer's "dosai" is not this one's.
    useEffect(() => {
        setPage(1); setSort(DEFAULT_ORDER_LOG_SORT);
        setPageSize(ORDER_LOG_PAGE_SIZE); setSearchInput(""); setSearch("");
    }, [customerId]);
    // A new sort, search or page size is a new view of the whole log: back to its first page.
    useEffect(() => { setPage(1); }, [sort.key, sort.dir, search, pageSize]);

    useEffect(() => {
        if (demo || !customerId || !scope.locationId || !scope.startDate || !scope.endDate) return;
        dispatch(fetchCustomerDetailsRequest({
            locationId: scope.locationId,
            customerId,
            ...sentDates(scope.live, scope.startDate, scope.endDate),
            page,
            pageSize,
            sortBy: sort.key,
            sortOrder: sort.dir,
            search,
        }));
    }, [dispatch, demo, customerId, scope.locationId, scope.live, scope.startDate, scope.endDate, page, pageSize, sort.key, sort.dir, search]);

    // Dropped on unmount so re-opening a row shows a spinner rather than the
    // previous customer's profile under the new one's name.
    useEffect(() => () => { dispatch(clearCustomerDetails()); }, [dispatch]);

    // Same toggle as the customer tables: the active column flips direction, a
    // new column starts descending (newest / largest first).
    const toggleSort = useCallback((key: OrderLogSortKey) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" })), []);

    return { data, loading, error, setPage, sort, toggleSort, pageSize, setPageSize, searchInput, setSearchInput, search };
}

type OrderLogSort = { key: OrderLogSortKey; dir: "asc" | "desc" };
const DEFAULT_ORDER_LOG_SORT: OrderLogSort = { key: "date", dir: "desc" };

/* ─── Customer Directory (CUST-8.1) — /customer-directory-kpi ─────────────
   Unlike the two KPI sections, the directory pages, sorts and searches, so its
   query is driven by the table's own state and lives inside `CustomerDirectory`.
   All it needs from up here is the outlet and the resolved period. */
interface DirectoryScope {
    locationId: string;
    /** Inclusive business dates, YYYY-MM-DD — "" until the period resolves. */
    startDate: string;
    endDate: string;
    /** Today: the dates above are still resolved, but are not SENT - see `useRequestPeriod`. */
    live: boolean;
    /** Demo mode: build the page from the mock instead of dispatching. */
    demo: boolean;
    /** The period as an export file names it ("Oct 1, 2026 – Oct 7, 2026", "Today"). */
    periodLabel: string;
}

/* The API's segment keys are the spec's; the mock `SEGMENTS` / `SEGMENT_INFO`
   use shorter ones. Map rather than rename either side, so the row keeps its
   colour and its CUST-3.2 tooltip. */
const SEGMENT_KEY_TO_MOCK: Record<string, string> = {
    champions: "champions",
    loyal_regulars: "loyal",
    promising: "promising",
    at_risk: "atrisk",
    slipping_away: "slipping",
    hibernating: "hibernating",
    lost: "lost",
    new: "new",
};

type KpiDelta = { text: string; arrow: "↑" | "↓" | ""; tone: "good" | "bad" | "flat" };

/* API deltas arrive pre-computed. Same convention as Snapshot, Sales and Labor:
   a delta the backend could not compute (`null` — no baseline) reads "0.00%"
   like any flat period, never "—"; every delta carries 2 decimals; the arrow
   follows the sign and only the colour says good or bad. `invert` is for KPIs
   where a fall is good news (Churn Rate, At Risk). */
function apiDelta(value: number | null | undefined, invert = false): KpiDelta {
    if (value == null || !Number.isFinite(value) || value === 0) return { text: "0.00%", arrow: "", tone: "flat" };
    const up = value > 0;
    return {
        text: `${up ? "+" : "-"}${Math.abs(value).toFixed(2)}%`,
        arrow: up ? "↑" : "↓",
        tone: (invert ? !up : up) ? "good" : "bad",
    };
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI CARD (CUST-1 / 2 / 4) — info icon + optional drill affordance
   ═══════════════════════════════════════════════════════════════════════════ */
function KPICard({ label, value, delta, deltaLoading, sub, info, drillable, onTap }: any) {
    const isDesktop = useIsDesktop();
    // `delta` omitted = a figure with no comparison (Avg CLV is all-time), so
    // no delta line is rendered at all rather than a misleading "0.00%".
    const d = delta as KpiDelta | undefined;
    const deltaColor = !d || d.tone === "flat" ? T.t2 : d.tone === "good" ? T.green : T.red;
    const deltaLabel = d ? `${d.arrow ? `${d.arrow} ` : ""}${d.text}` : "";
    if (isDesktop) {
        return (
            <div
                onClick={drillable ? onTap : undefined}
                style={{
                    flex: "1 1 150px", minWidth: 150, position: "relative",
                    background: T.surface, borderRadius: 14, padding: "18px 20px",
                    border: `1px solid ${drillable ? T.accent + "33" : T.cardBorder}`, boxShadow: T.cardShadow,
                    cursor: drillable ? "pointer" : "default",
                }}
            >
                {drillable && <Arrow />}
                <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, letterSpacing: 0.5, textTransform: "uppercase", display: "flex", alignItems: "center", marginBottom: 8 }}>
                    {label}{info && <InfoTip text={info} />}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: T.t1, letterSpacing: -0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
                {!d ? null : deltaLoading ? (
                    <div style={{ marginTop: 6 }}><Bone w={56} h={12} r={4} /></div>
                ) : (
                    <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 6, display: "flex", alignItems: "center", gap: 3 }}>
                        {deltaLabel}
                    </div>
                )}
                {sub && <div style={{ fontSize: 10, color: T.t3, marginTop: 4 }}>{sub}</div>}
            </div>
        );
    }
    return (
        <div
            onClick={drillable ? onTap : undefined}
            style={{
                position: "relative", background: T.surface, borderRadius: 12, padding: "12px 10px", minWidth: 0,
                border: `1px solid ${drillable ? T.accent + "33" : T.cardBorder}`, boxShadow: T.shadow,
                textAlign: "center", cursor: drillable ? "pointer" : "default",
            }}
        >
            {drillable && <Arrow />}
            <div style={{ fontSize: 8, fontWeight: 500, color: T.t3, letterSpacing: 0.3, textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                {label}{info && <InfoTip text={info} />}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
            {!d ? null : deltaLoading
                ? <div style={{ marginTop: 3, display: "flex", justifyContent: "center" }}><Bone w={36} h={9} r={3} /></div>
                : <div style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 3 }}>{deltaLabel}</div>}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-2.2 — AT-RISK ALERT BANNER
   "shows: count, delta vs. prior period, and the single highest-CLV at-risk
   customer's name, visit count, CLV, and days overdue". Desktop: "Orange alert
   banner below KPI row with 'View At-Risk List' button". Mobile: "Compact
   banner with chevron drill-in". Both open the At Risk card's own drill, which
   lists `churn_status = 'at_risk'` sorted by Total Spend (CLV) descending.
   The count and delta are the At Risk card's, so the two can never disagree.
   ═══════════════════════════════════════════════════════════════════════════ */
function AtRiskBanner({ count, delta, top, onOpen }: {
    count: number;
    delta: KpiDelta;
    top: TopAtRiskCustomer | null | undefined;
    onOpen: () => void;
}) {
    const isDesktop = useIsDesktop();
    const money = useMoney();
    const deltaColor = delta.tone === "flat" ? T.t2 : delta.tone === "good" ? T.green : T.red;
    const deltaLabel = `${delta.arrow ? `${delta.arrow} ` : ""}${delta.text}`;
    const name = top?.name?.trim() || "Unnamed customer";
    const days = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} day${n === 1 ? "" : "s"}`;
    const headline = `${count.toLocaleString()} customer${count === 1 ? "" : "s"} at risk`;
    // CUST-2.2 lists "days overdue" among what the banner shows, so the detail
    // line wraps rather than ending in an ellipsis: it breaks only at the " · "
    // between facts, and each fact stays whole ("8 days overdue" never splits).
    const overdue = <span style={{ color: T.orange, fontWeight: 600 }}>{days(top?.daysOverdue ?? 0)} overdue</span>;
    const facts = (items: ReactNode[]) =>
        items.flatMap((item, i) => [" · ", <span key={i} style={{ whiteSpace: "nowrap" }}>{item}</span>]);

    if (isDesktop) {
        return (
            <div role="status" style={{
                display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 14,
                background: T.orangeSoft, border: `1px solid ${T.orange}40`,
            }}>
                <Icon name="warning" size={22} color={T.orange} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{headline}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: deltaColor }}>{deltaLabel}</span>
                        <span style={{ fontSize: 11.5, color: T.t3 }}>vs prior period</span>
                    </div>
                    {top && (
                        <div style={{ fontSize: 12.5, color: T.t2, marginTop: 3, lineHeight: 1.45 }}>
                            Highest value: <strong style={{ color: T.t1 }}>{name}</strong>
                            {facts([
                                `${top.visits.toLocaleString()} visit${top.visits === 1 ? "" : "s"}`,
                                `${money(top.clv)} CLV`,
                                overdue,
                            ])}
                        </div>
                    )}
                </div>
                <button type="button" onClick={onOpen} style={{
                    display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0, padding: "8px 14px",
                    borderRadius: 8, border: "none", background: T.orange, color: T.surface,
                    fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer",
                }}>
                    View At-Risk List
                    <Icon name="chevron_right" size={16} color={T.surface} />
                </button>
            </div>
        );
    }

    return (
        <button type="button" onClick={onOpen} aria-label={`${headline} - view the At Risk list`} style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 12,
            background: T.orangeSoft, border: `1px solid ${T.orange}40`, textAlign: "left", fontFamily: T.font, cursor: "pointer",
        }}>
            <Icon name="warning" size={18} color={T.orange} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: T.t1 }}>{count.toLocaleString()} at risk</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: deltaColor }}>{deltaLabel}</span>
                </div>
                {top && (
                    <div style={{ fontSize: 11.5, color: T.t2, marginTop: 2, lineHeight: 1.45 }}>
                        {name}{facts([money(top.clv), overdue])}
                    </div>
                )}
            </div>
            <Icon name="chevron_right" size={18} color={T.t3} />
        </button>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-3.4 — SEGMENT REFRESH CONTROL (freshness label + Refresh Segments)
   Rendered in the Segment Breakdown card header. Disabled — for every viewer of
   the client, not just the one who clicked — while a refresh is in flight.
   ═══════════════════════════════════════════════════════════════════════════ */
function SegmentRefreshControl({ sync, refreshedAt, onRefresh }: any) {
    const isDesktop = useIsDesktop();
    // `refreshedAt` is when the scores were actually rebuilt, which is what the
    // spec's "Segments as of …" means. The local sync clock is only the
    // fallback while that is unknown.
    const scoredAt = refreshedAt ? Date.parse(refreshedAt) : NaN;
    const hasScoredAt = Number.isFinite(scoredAt);
    const label = sync.refreshing
        ? "Refresh in progress…"
        : hasScoredAt
            ? `Segments as of ${formatFreshness(scoredAt)}`
            : refreshedAt === null
                ? "Segments not yet computed"
                : sync.neverComputed
                    ? "Segments not yet computed — click Refresh to generate"
                    : `Segments as of ${sync.freshness}`;
    const exact = hasScoredAt ? exactTimestamp(scoredAt) : sync.exact;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span title={exact || undefined} style={{ fontSize: 11, color: sync.refreshing ? T.accent : T.t3, whiteSpace: "nowrap" }}>
                {label}
            </span>
            <button
                onClick={onRefresh}
                disabled={sync.refreshing}
                style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8,
                    border: `1px solid ${sync.refreshing ? T.cardBorder : T.accent + "44"}`,
                    background: sync.refreshing ? T.hover : T.surface,
                    color: sync.refreshing ? T.t3 : T.accent,
                    cursor: sync.refreshing ? "default" : "pointer",
                    fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                }}
            >
                <Icon name={sync.refreshing ? "progress_activity" : "refresh"} size={15} color={sync.refreshing ? T.t3 : T.accent}
                    style={sync.refreshing ? { animation: "spin 0.9s linear infinite" } : undefined} />
                {sync.refreshing ? "Refreshing…" : isDesktop ? "Refresh Segments" : "Refresh"}
            </button>
        </div>
    );
}

/* Placeholder shown until a client's first successful refresh (CUST-3.4). */
function SegmentsNotComputed({ onRefresh, refreshing }: any) {
    return (
        <div style={{ padding: "28px 16px", textAlign: "center", color: T.t3 }}>
            <Icon name="donut_large" size={26} color={T.t3} />
            <div style={{ fontSize: 13, marginTop: 8, color: T.t2 }}>Segments not yet computed for this location.</div>
            <button onClick={onRefresh} disabled={refreshing} style={{ marginTop: 12, padding: "8px 18px", borderRadius: 8, border: `1px solid ${T.accent}44`, background: T.surface, color: T.accent, cursor: refreshing ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}>
                {refreshing ? "Refreshing…" : "Refresh Segments"}
            </button>
        </div>
    );
}

/* Cooldown confirm — discourages needless repeated recomputes. */
function CooldownPrompt({ minutesAgo, onConfirm, onCancel }: any) {
    return (
        <div onClick={onCancel} style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,18,30,0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: 16, width: 380, maxWidth: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.28)", padding: "20px 20px 16px" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>Refresh again?</div>
                <div style={{ fontSize: 12.5, color: T.t2, lineHeight: 1.5, marginTop: 8 }}>
                    Segments were refreshed {minutesAgo === 0 ? "less than a minute" : minutesAgo === 1 ? "1 minute" : `${minutesAgo} minutes`} ago. Refresh again?
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                    <button onClick={onCancel} style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t2, cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>Cancel</button>
                    <button onClick={onConfirm} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: T.accent, color: "#fff", cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>Refresh again</button>
                </div>
            </div>
        </div>
    );
}

/* Non-blocking failure toast — last-good segments stay on screen. */
function Toast({ message }: { message: string }) {
    return (
        <div style={{
            position: "fixed", left: "50%", bottom: 28, transform: "translateX(-50%)", zIndex: 5200,
            background: T.t1, color: "#fff", borderRadius: 10, padding: "11px 16px", fontSize: 12.5, fontWeight: 500,
            boxShadow: "0 8px 28px rgba(0,0,0,0.28)", display: "flex", alignItems: "center", gap: 8, maxWidth: "90vw",
        }}>
            <Icon name="error" size={16} color={T.orange} fill={1} />{message}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
function Dashboard({ stampKpis, segments, channels, cohorts, segmentSpend, acquisition, directoryScope, demoMode, setDemoMode, openDrill, openProfile, openExport, sync }: {
    stampKpis: ReturnType<typeof useCustomerStampKpis>;
    segments: ReturnType<typeof useCustomerSegments>;
    channels: ReturnType<typeof useChannelPerformance>;
    cohorts: ReturnType<typeof useCohortRetention>;
    segmentSpend: ReturnType<typeof useSegmentSpend>;
    acquisition: ReturnType<typeof useAcquisitionSources>;
    directoryScope: DirectoryScope;
    demoMode: boolean;
    setDemoMode: (on: boolean) => void;
    openDrill: (d: DrillSpec) => void;
    openProfile: (id: string) => void;
    openExport: (spec: ExportFlowSpec) => void;
    sync: any;
}) {
    const isDesktop = useIsDesktop();
    const money = useMoney();

    /* ── CUST-3.4 refresh interactions ── */
    const [cooldownPrompt, setCooldownPrompt] = useState<number | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    // Bumped after Refresh Segments so the Customer Directory re-queries too: it
    // owns its own fetch (page / sort / search), so there is no hook to call.
    const [directoryRefreshKey, setDirectoryRefreshKey] = useState(0);

    /*
     * Refresh Segments rebuilds `fact_customers`, which nearly every card reads
     * (first / last visit, R/F/M, names), so after it EVERY card on the dashboard
     * reloads - stamp KPIs, segments, avg spend, cohorts, channels, acquisition
     * and the directory - not just the two segment cards.
     */
    const reloadAllCards = useCallback(() => {
        stampKpis.refetch();
        segments.refetch();
        segmentSpend.refetch();
        cohorts.refetch();
        channels.refetch();
        acquisition.refetch();
        setDirectoryRefreshKey((k) => k + 1);
    }, [stampKpis, segments, segmentSpend, cohorts, channels, acquisition]);

    /** Resolves once `refreshedAt` moves past `before` (the rebuild finished), or after REBUILD_WAIT_MS. */
    const waitForRebuild = useCallback(async (before: string | null | undefined) => {
        const locationId = directoryScope.locationId;
        if (!locationId) return false;
        const deadline = Date.now() + REBUILD_WAIT_MS;
        while (Date.now() < deadline) {
            await new Promise((r) => window.setTimeout(r, REBUILD_POLL_MS));
            try {
                const latest = await getCustomerSegmentBreakdownApi({ locationId });
                if (latest.refreshedAt && latest.refreshedAt !== before) return true;
            } catch {
                // A failed poll is not a failed refresh - keep waiting until the deadline.
            }
        }
        return false;
    }, [directoryScope.locationId]);
    const prevSyncState = useRef(sync.status.state);

    // Surface a toast only on a real in_progress → failed transition, so a
    // stale `failed` record from a previous session doesn't nag on mount.
    useEffect(() => {
        if (prevSyncState.current === "in_progress" && sync.status.state === "failed") {
            setToast("Segment refresh failed. Please try again.");
        }
        prevSyncState.current = sync.status.state;
    }, [sync.status.state]);

    useEffect(() => {
        if (!toast) return;
        const id = window.setTimeout(() => setToast(null), 4000);
        return () => window.clearTimeout(id);
    }, [toast]);

    const handleRefresh = useCallback(async (force = false) => {
        const res = sync.refresh({ force });
        // Only the cooldown case needs a decision from the user; an in-flight
        // refresh simply leaves the shared disabled state as-is.
        if (!res.started && res.reason === "cooldown") setCooldownPrompt(res.minutesAgo);
        else setCooldownPrompt(null);
        if (!res.started) return;

        // The button now genuinely recomputes: CUST-3.4's endpoint runs
        // SYSTEM REFRESH VIEW on the MV behind `fact_customers`. `sync` is kept
        // for the cross-tab cooldown and lock it already handles.
        if (demoMode) { reloadAllCards(); return; }

        const before = segments.data?.refreshedAt ?? null;
        let started = false;
        try {
            const result = await refreshSegmentBreakdownApi();
            // `triggered: false` means someone else's rebuild is already
            // running — a normal outcome, not a failure; still wait for it.
            if (!result.triggered) setToast("A refresh is already running — showing the latest scores.");
            started = true;
        } catch (error: any) {
            setToast(error?.message || "Segment refresh failed. Please try again.");
        }
        // Wait for the rebuild to land before re-reading, or every card would
        // just fetch the old numbers again.
        if (started) {
            const done = await waitForRebuild(before);
            if (!done) setToast("Refresh is taking longer than usual — numbers will update on the next reload.");
        }
        // Re-read either way: on success to pick up the new data, on failure so
        // the cards still reflect whatever is actually in the table.
        reloadAllCards();
    }, [sync, segments.data?.refreshedAt, demoMode, reloadAllCards, waitForRebuild]);

    /* ── KPI row (6) ── */
    const k = stampKpis.data;
    const kpiValue = (text: string) => (stampKpis.loading ? <Bone w={isDesktop ? 80 : 44} h={isDesktop ? 24 : 18} r={6} /> : k ? text : "—");
    // A KPI with nothing behind it (loading, failed, null or 0) must not offer a drill-down.
    const hasValue = (v: number | null | undefined) => !stampKpis.loading && typeof v === "number" && v > 0;
    const newCount = (k?.newCustomers ?? 0).toLocaleString();
    const returningCount = (k?.returningCustomers ?? 0).toLocaleString();
    const newDelta = apiDelta(k?.newCustomersDeltaPercent);
    const retDelta = apiDelta(k?.returningCustomersDeltaPercent);
    const rrDelta = apiDelta(k?.thirtyDayReturnRateDeltaPp);
    const churnD = apiDelta(k?.churnRateDeltaPp, true);
    const atRiskD = apiDelta(k?.atRiskCustomersDeltaPercent, true);
    // The At Risk card and the CUST-2.2 banner open the same list.
    const openAtRisk = () => openDrill({ key: "at-risk", drill: "at_risk", badge: { label: "At Risk", color: T.orange } });

    /* ── Segment breakdown (live) ── */
    const seg = segments.data;
    const totalCustomers = seg?.totalCustomers ?? 0;
    // One row per API segment, carrying the mock's colour + CUST-3.2 tooltip.
    const segRows = (seg?.segments ?? []).map((row) => {
        const mockKey = SEGMENT_KEY_TO_MOCK[row.key] ?? row.key;
        const mock = SEGMENTS.find((s) => s.key === mockKey);
        return {
            key: row.key,
            infoKey: mockKey,
            name: row.name,
            color: mock?.color ?? T.t3,
            count: row.customerCount,
            pct: row.percentOfTotal,
            avgClv: row.avgClv,
            avgVisits: row.avgVisits12m,
        };
    });

    const openSegmentDrill = (segName: string) => {
        const row = segRows.find((s) => s.name === segName);
        const mock = SEGMENTS.find((s) => s.name === segName);
        openDrill({
            key: `seg-${row?.key ?? segName}`,
            drill: "segment",
            segmentKey: row?.key,
            badge: { label: segName, color: row?.color ?? mock?.color ?? T.t3 },
        });
    };

    /* ── Avg spend by segment (sorted) ── */
    /* CUST-12.1 wants the widest bar first, so the client sorts — the API
       returns spec order. Quiet segments (null AOV) sort last: they have no
       bar to be widest. */
    const spend = segmentSpend.data;
    const spendRows = useMemo(
        () => [...(spend?.segments ?? [])].sort(
            (a, b) => (b.avgOrderValue ?? -1) - (a.avgOrderValue ?? -1),
        ),
        [spend],
    );
    const maxSpend = Math.max(...spendRows.map((r) => r.avgOrderValue ?? 0), 1);

    /* ── Channel performance ── */
    /* Bars are scaled against the BIGGEST channel, not the total — the top
       channel always fills the row. Guarded at 1 so an all-zero period can't
       divide by zero. */
    const chan = channels.data;
    const chanRows = chan?.channels ?? [];
    const maxChanCust = Math.max(...chanRows.map((c) => c.customers), 1);

    /* Column count comes from the API's widest row, not a hard-coded M0–M5:
       the window is 12 months, so a mature outlet fills M0–M11. */
    const coh = cohorts.data;
    const cohortRows = coh?.cohorts ?? [];
    const cohortCols = useMemo(
        () => Array.from({ length: (coh?.maxMonthOffset ?? 0) + 1 }, (_, i) => i),
        [coh?.maxMonthOffset],
    );

    /* ── Acquisition source (sortable) ── */
    const [acqSort, setAcqSort] = useState<{ key: "newCount" | "repeatRate" | "avgClv"; dir: "asc" | "desc" }>({ key: "newCount", dir: "desc" });
    /* The endpoint returns only the sources that brought in NEW customers in the
       period (QA #19) — the CUST-19.1 categories plus any discovered `utm_*`
       source — and CUST-19.1 makes sorting
       the client's job, so this sorts what came back. Icons come from the local
       table keyed by `sourceKey` — they are presentation, and the API has no
       business shipping Material icon names; a discovered source has no entry
       there and gets the default icon, with the API's `sourceName` as its label.

       `avgClv` and the drill cover every customer the source ever brought in
       (all-time, as of today), not just this period. A null rate / CLV still
       sorts as -1 so it sinks rather than tying with a real 0%. */
    const acqRows = useMemo(() => {
        const rows = (acquisition.data?.sources ?? []).map((row) => ({
            key: row.sourceKey,
            source: row.sourceName,
            icon: acquisitionByKey(row.sourceKey)?.icon ?? "travel_explore",
            newCount: row.newCustomers,
            repeatRate: row.repeatRatePercent,
            avgClv: row.avgClv,
            historical: row.historicalCustomers,
            smallSample: row.smallSample,
        }));
        const dir = acqSort.dir === "asc" ? 1 : -1;
        const at = (r: (typeof rows)[number]) =>
            acqSort.key === "newCount" ? r.newCount : (r[acqSort.key] ?? -1);
        return rows.sort((a, b) => (at(a) - at(b)) * dir);
    }, [acqSort, acquisition.data]);
    const toggleAcq = (key: "newCount" | "repeatRate" | "avgClv") =>
        setAcqSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

    /* ── Complaints / cancellations ── */
    const complaintRate = ((COMPLAINTS.total.current / COMPLAINTS.orders) * 100).toFixed(1);
    const complaintDelta = COMPLAINTS.total.current - COMPLAINTS.total.prior;
    const cancelRate = ((CANCELLATIONS.orders.current / CANCELLATIONS.totalOrders) * 100).toFixed(1);
    const cancelDelta = CANCELLATIONS.orders.current - CANCELLATIONS.orders.prior;
    const maxComplaint = Math.max(...COMPLAINTS.reasons.map((r) => r.count), 1);

    // Issues & Chargebacks section is hidden for now — flip to true to restore.
    const SHOW_ISSUES = false;

    /* Acquisition Source (CUST-19.1) is live. `/acquisition-source-kpi` reads the
       UTM data on each customer's first order (backend `8fc06dc`), so new
       marketing sources show up as rows on their own.

       Expect Walk-in / Unattributed to hold almost every customer for now — only
       a handful of orders carry UTM data yet, so mostly Marketplace and Direct
       (from the order's channel) can be told apart.
       CUST-19.1 says that bucket "will typically be the largest ... and should
       not be treated as a failure of attribution", so the card states it rather
       than looking broken. */
    const SHOW_ACQUISITION = true;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: isDesktop ? 20 : 12 }}>
            <DemoModeBar demoMode={demoMode} setDemoMode={setDemoMode} isDesktop={isDesktop} />

            {/* ═══ KPI ROW (CUST-1 / 2 / 4) ═══ */}
            <div style={{ display: "flex", gap: isDesktop ? 16 : 8, flexWrap: "wrap" }}>
                {stampKpis.error && !stampKpis.loading && (
                    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                        <Icon name="error" size={15} color={T.red} />
                        <span style={{ flex: 1 }}>Couldn't load customer KPIs. Please try again.</span>
                        <button onClick={stampKpis.refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                    </div>
                )}
                <div style={{ display: isDesktop ? "flex" : "grid", gridTemplateColumns: isDesktop ? undefined : "1fr 1fr 1fr", gap: isDesktop ? 16 : 8, flex: 1, flexWrap: "wrap", width: "100%" }}>
                    <KPICard label="New Customers" value={kpiValue(newCount)} info={infoCopy("newCustomers")} icon="person_add" sub="First-ever order this period"
                        delta={newDelta} deltaLoading={stampKpis.loading}
                        drillable={hasValue(k?.newCustomers)} onTap={() => openDrill({ key: "new", drill: "new", badge: { label: "New", color: "#0EA5E9" } })} />
                    <KPICard label="Returning" value={kpiValue(returningCount)} info={infoCopy("returning")} icon="loyalty" sub="Repeat guest base"
                        delta={retDelta} deltaLoading={stampKpis.loading}
                        drillable={hasValue(k?.returningCustomers)} onTap={() => openDrill({ key: "returning", drill: "returning", badge: { label: "Returning", color: T.green } })} />
                    {/* The sub-label used to read "Industry avg: 25%". That number
                        came from the mock config, not from any measurement of this
                        or any other restaurant — an invented benchmark sitting
                        directly under a real rate, which is the one place a made-up
                        figure does most damage. Replaced with what the rate
                        actually measures. */}
                    <KPICard label="30-Day Return Rate" value={kpiValue(k?.thirtyDayReturnRate != null ? `${k.thirtyDayReturnRate}%` : "—")} info={infoCopy("returnRate")} icon="replay_30"
                        /* QA #55: name the cohort. CUST-1.3 measures guests acquired in a window that
                           ends 30 days before the period does, so this rate and the Acquisition
                           Source card's 30-day repeat (this period's own new customers) are
                           different measures. The window is already on the response. */
                        sub={k?.acquisitionWindowStartDate && k?.acquisitionWindowEndDate
                            ? `Acquired ${comparisonDaysLabel(k.acquisitionWindowStartDate, k.acquisitionWindowEndDate)}, back within 30 days`
                            : "Returned within 30 days"}
                        delta={rrDelta} deltaLoading={stampKpis.loading} />
                    <KPICard label="Churn Rate" value={kpiValue(k?.churnRate != null ? `${k.churnRate}%` : "—")} info={infoCopy("churnRate")} icon="person_off" sub={`${k?.churnThresholdDays ?? CUST_CONFIG.churnThresholdDays}-day threshold`}
                        delta={churnD} deltaLoading={stampKpis.loading} />
                    {/* All-time net sales per customer: does not follow the date filter, so no delta. */}
                    <KPICard label="Avg CLV" value={kpiValue(money(k?.avgClv))} info={infoCopy("avgClv")} icon="diamond" sub="All-time net sales per customer" />
                    <KPICard label="At Risk" value={kpiValue((k?.atRiskCustomers ?? 0).toLocaleString())} info={infoCopy("atRisk")} icon="warning" sub="Win-back priority"
                        delta={atRiskD} deltaLoading={stampKpis.loading}
                        drillable={hasValue(k?.atRiskCustomers)} onTap={openAtRisk} />
                </div>
            </div>

            {/* ═══ CUST-2.2 AT-RISK ALERT BANNER — below the KPI row ═══
                Only when there is something to alert: not while loading, not on
                an error, and not with 0 at risk (the story is silent on zero; an
                empty alert is noise). An older backend without
                `topAtRiskCustomer` still gets the count line. */}
            {!stampKpis.loading && !stampKpis.error && hasValue(k?.atRiskCustomers) && (
                <AtRiskBanner count={k!.atRiskCustomers} delta={atRiskD} top={k!.topAtRiskCustomer} onOpen={openAtRisk} />
            )}

            {/* ═══ SEGMENT BREAKDOWN (CUST-3.3) — no blocks viz ═══ */}
            <SectionCard title="Segment Breakdown" info={infoCopy("segmentBreakdown")} icon="table_chart"
                subtitle={segments.loading
                    ? "Loading segments…"
                    : seg
                        ? `${totalCustomers.toLocaleString()} customers across ${seg.segmentCount} segment${seg.segmentCount === 1 ? "" : "s"} — click a row to view customers`
                        : "Segments unavailable"}
                right={<SegmentRefreshControl sync={sync} refreshedAt={seg?.refreshedAt ?? null} onRefresh={() => handleRefresh(false)} />}>
                {segments.error && !segments.loading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                        <Icon name="error" size={15} color={T.red} />
                        <span style={{ flex: 1 }}>Couldn't load segments. Please try again.</span>
                        <button onClick={segments.refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                    </div>
                ) : (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                        <thead>
                            <tr>{["Segment", "Customers", "% of Total", "Avg CLV", "Avg Visits", ""].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {segments.loading
                                ? Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                        {Array.from({ length: 6 }).map((__, j) => (
                                            <td key={j} style={tdCell}><Bone w={j === 0 ? 120 : 56} h={12} r={4} /></td>
                                        ))}
                                    </tr>
                                ))
                                : segRows.map((row) => {
                                    // An empty segment has no customers to list — no chevron, no click.
                                    const drillable = row.count > 0;
                                    return (
                                        <tr key={row.key} onClick={drillable ? () => openSegmentDrill(row.name) : undefined}
                                            style={{ borderBottom: `1px solid ${T.divider}`, cursor: drillable ? "pointer" : "default" }}
                                            onMouseEnter={(e) => { if (drillable) e.currentTarget.style.background = T.hover; }}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                            <td style={tdCell}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                    <InfoTip text={SEGMENT_INFO[row.infoKey]} />
                                                    <SegmentBadge label={row.name} color={row.color} />
                                                </span>
                                            </td>
                                            <td style={{ ...tdCell, fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                                            <td style={{ ...tdCell, color: T.t2 }}>{row.pct.toFixed(1)}%</td>
                                            <td style={{ ...tdCell, fontWeight: 600 }}>{row.avgClv != null ? money(row.avgClv) : "—"}</td>
                                            <td style={{ ...tdCell, color: T.t2 }}>{row.avgVisits != null ? row.avgVisits : "—"}</td>
                                            <td style={{ ...tdCell, textAlign: "right" }}>
                                                {drillable && <Icon name="chevron_right" size={16} color={T.t3} />}
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                    {/* The 8 rows deliberately don't sum to the total: customers whose
                        scores match no CUST-3.2 rule are counted here instead. */}
                    {!segments.loading && !!seg?.otherCustomers && (
                        <div style={{ fontSize: 11, color: T.t3, padding: "8px 12px 0" }}>
                            + {seg.otherCustomers.toLocaleString()} customers not matching any segment rule
                        </div>
                    )}
                    {/* CUST-3.1 (QA #2): with fewer than 5 customers quintiles are
                        meaningless — every customer is neutral 3/3/3 and the PO
                        placed them all in New. Say so, or the lone New row reads
                        as a data error. */}
                    {!segments.loading && !!seg?.insufficientData && (
                        <div style={{ fontSize: 11, color: T.t3, padding: "8px 12px 0" }}>
                            Insufficient data for segmentation — fewer than 5 customers at this outlet — every customer gets neutral R3 / F3 / M3 scores and is shown as New.
                        </div>
                    )}
                </div>
                )}
            </SectionCard>

            {/* ═══ COHORT RETENTION (CUST-11) + AVG SPEND BY SEGMENT (CUST-12) ═══ */}
            <div style={{ display: "flex", gap: isDesktop ? 20 : 12, flexDirection: isDesktop ? "row" : "column" }}>
                <SectionCard title="Cohort Retention" info={infoCopy("cohort")} icon="grid_on"
                    subtitle={cohorts.loading
                        ? "Loading cohorts…"
                        : coh
                            ? `${coh.totalCustomers.toLocaleString()} guests acquired over ${coh.windowMonths} months — share returning each later month`
                            : "Cohorts unavailable"}
                    style={{ flex: 1.3, minWidth: 0 }}>
                    {cohorts.error && !cohorts.loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                            <Icon name="error" size={15} color={T.red} />
                            <span style={{ flex: 1 }}>Couldn't load cohort retention. Please try again.</span>
                            <button onClick={cohorts.refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                        </div>
                    ) : cohorts.loading ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {Array.from({ length: 6 }).map((_, i) => <Bone key={i} w="100%" h={26} r={4} />)}
                        </div>
                    ) : cohortRows.length === 0 ? (
                        <div style={{ padding: "24px 16px", textAlign: "center", color: T.t3, fontSize: 13 }}>
                            No customers acquired in this window
                        </div>
                    ) : (
                        <>
                            {/* Mobile scrolls horizontally rather than dropping columns —
                                CUST-11.1 asks for the full heatmap on desktop and a
                                scrollable one with compact M headers on mobile. */}
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 3, minWidth: 64 + cohortCols.length * 46 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: 9, fontWeight: 600, color: T.t3, textAlign: "left", padding: "4px 8px", minWidth: 64 }}>COHORT</th>
                                            {cohortCols.map((m) => <th key={m} style={{ fontSize: 9, fontWeight: 500, color: T.t3, textAlign: "center", padding: "4px 6px" }}>M{m}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cohortRows.map((row) => {
                                            // Cells are dense 0..monthsElapsed, but index them anyway:
                                            // a missing offset must render "—", never shift the row left.
                                            const byOffset = new Map(row.cells.map((c) => [c.monthOffset, c]));
                                            return (
                                                <tr key={row.cohortMonth}>
                                                    <td style={{ fontSize: 11, fontWeight: 500, color: T.t2, padding: "4px 8px", whiteSpace: "nowrap" }}>
                                                        {/* CUST-11.1's row label: "Sep '25 (420)" - the month's new
                                                            customers, equal to the New Customers card for that
                                                            month (QA #23). */}
                                                        {row.cohortLabel}{" "}
                                                        <span style={{ color: T.t3, fontWeight: 400 }}>({row.cohortSize.toLocaleString()})</span>
                                                        {row.smallCohort && (
                                                            <TapTip inline width={200} content="Small cohort — interpret with caution." style={{ marginLeft: 3, display: "inline-flex", verticalAlign: "middle" }}>
                                                                <Icon name="warning" size={11} color={T.orange} />
                                                            </TapTip>
                                                        )}
                                                    </td>
                                                    {cohortCols.map((offset) => {
                                                        const cell = byOffset.get(offset);
                                                        // The API sends one decimal; CUST-11.1 displays 31.9 as "32%".
                                                        const pct = cell ? Math.round(cell.retentionPercent) : null;
                                                        return (
                                                            <td key={offset} style={{ textAlign: "center", padding: 0 }}>
                                                                {/* The report's own tooltip, not `title`: hover on desktop,
                                                                    tap in the owner app, where a phone has no hover (QA #22). */}
                                                                <TapTip
                                                                    content={
                                                                        <>
                                                                            <div style={{ fontWeight: 700, color: T.t1 }}>{row.cohortLabel} · M{offset}</div>
                                                                            {cell ? (
                                                                                <div>{pct}% ({cell.activeCustomers.toLocaleString()} of {row.cohortSize.toLocaleString()} guests) ordered {offset === 0 ? "in the month they were acquired" : `${offset} month${offset === 1 ? "" : "s"} later`}</div>
                                                                            ) : row.cohortSize === 0 ? (
                                                                                /* QA #52: a cell is also empty when NOBODY was acquired
                                                                                   that month - the backend sends no cells for a cohort of
                                                                                   0 - and telling the operator a past month "hasn't
                                                                                   happened yet" is simply false. The row keeps its place
                                                                                   so the grid stays 12 rows (CUST-11.1). */
                                                                                <div>No guests were acquired in this month, so there is nothing to retain.</div>
                                                                            ) : (
                                                                                <div>Not yet available — this month hasn't happened yet.</div>
                                                                            )}
                                                                        </>
                                                                    }
                                                                    style={{ background: cohortColor(pct), borderRadius: 4, padding: "8px 4px", fontSize: 11, fontWeight: cell ? 600 : 400, color: cell ? ((pct ?? 0) >= 30 ? "#fff" : T.t1) : T.t3 }}>
                                                                    {cell ? `${pct}%` : "—"}
                                                                </TapTip>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontSize: 10, color: T.t3 }}>Retention:</span>
                                {[{ label: "30%+", bg: T.green }, { label: "20–30%", bg: "rgba(22,163,74,0.55)" }, { label: "15–20%", bg: "rgba(22,163,74,0.35)" }, { label: "10–15%", bg: "rgba(22,163,74,0.2)" }, { label: "<10%", bg: "rgba(22,163,74,0.08)" }].map((l) => (
                                    <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{ width: 14, height: 10, borderRadius: 2, background: l.bg }} /><span style={{ fontSize: 9, color: T.t3 }}>{l.label}</span>
                                    </div>
                                ))}
                            </div>
                            {/* Retention is not cumulative (CUST-11.1) — without saying so, a
                                cell that rises after a dip reads as a data error. */}
                            <div style={{ marginTop: 10, fontSize: 10.5, color: T.t3, display: "flex", alignItems: "flex-start", gap: 6 }}>
                                <Icon name="info" size={13} color={T.t3} />
                                <span>
                                    Each cell is the share ordering in that month alone, not a running total —
                                    a guest who skips a month and returns the next counts only in the later one.
                                    “—” means either that nobody was acquired in that cohort's month,
                                    or that the month hasn't happened yet — the cell's tooltip says which.
                                </span>
                            </div>
                        </>
                    )}
                </SectionCard>


                <SectionCard title="Avg Spend by Segment" info={infoCopy("avgSpendBySegment")} icon="payments"
                    subtitle={segmentSpend.loading
                        ? "Loading segment spend…"
                        : spend
                            ? `Average order value per RFM segment — change vs ${comparisonDaysLabel(spend.priorStartDate, spend.priorEndDate)}${spend.overallAvgOrderValue !== null ? `, ${money(spend.overallAvgOrderValue)} overall` : ""}`
                            : "Average order value per RFM segment"}
                    style={{ flex: 1, minWidth: 0 }}>
                    {sync.neverComputed && !demoMode ? (
                        <SegmentsNotComputed onRefresh={() => handleRefresh(true)} refreshing={sync.refreshing} />
                    ) : segmentSpend.error && !segmentSpend.loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                            <Icon name="error" size={15} color={T.red} />
                            <span style={{ flex: 1 }}>Couldn't load segment spend. Please try again.</span>
                            <button onClick={segmentSpend.refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                        </div>
                    ) : segmentSpend.loading ? (
                        Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                    <Bone w={110} h={12} r={4} /><Bone w={86} h={12} r={4} />
                                </div>
                                <Bone w="100%" h={8} r={4} />
                            </div>
                        ))
                    ) : (
                        spendRows.map((row) => {
                            // CUST-12.1: a segment with no orders shows "—" and NO bar.
                            // `null` is not 0 — a 0 would draw a bar and read as
                            // "they ordered and spent nothing".
                            const quiet = row.avgOrderValue === null;
                            return (
                                <BarRow key={row.key}
                                    label={<span title={`${row.orders.toLocaleString()} order${row.orders === 1 ? "" : "s"} this period, ${row.priorOrders.toLocaleString()} in the previous`}>{row.name}</span> as any}
                                    display={quiet ? "—" : money(row.avgOrderValue as number)}
                                    sub={<SpendDelta delta={row.delta} priorOrders={row.priorOrders} /> as any}
                                    pct={quiet ? 0 : ((row.avgOrderValue as number) / maxSpend) * 100}
                                    color={segmentColor(row.name)} />
                            );
                        })
                    )}
                </SectionCard>
            </div>

            {/* ═══ CHANNEL PERFORMANCE (CUST-13) + ACQUISITION SOURCE (CUST-19.1, hidden) ═══ */}
            <div style={{ display: "flex", gap: isDesktop ? 20 : 12, flexDirection: isDesktop ? "row" : "column" }}>
                <SectionCard title="Channel Performance" info={infoCopy("channelPerformance")} icon="hub"
                    subtitle={channels.loading
                        ? "Loading channels…"
                        : chan
                            ? `${chan.distinctCustomers.toLocaleString()} guest${chan.distinctCustomers === 1 ? "" : "s"} across ${chanRows.length} channel${chanRows.length === 1 ? "" : "s"}`
                            : "Channels unavailable"}
                    style={{ flex: 1, minWidth: 0 }}>
                    {channels.error && !channels.loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                            <Icon name="error" size={15} color={T.red} />
                            <span style={{ flex: 1 }}>Couldn't load channel performance. Please try again.</span>
                            <button onClick={channels.refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                        </div>
                    ) : channels.loading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                    <Bone w={96} h={12} r={4} /><Bone w={72} h={12} r={4} />
                                </div>
                                <Bone w="100%" h={8} r={4} />
                            </div>
                        ))
                    ) : chanRows.length === 0 ? (
                        <div style={{ padding: "24px 16px", textAlign: "center", color: T.t3, fontSize: 13 }}>
                            No orders in this period
                        </div>
                    ) : (
                        <>
                            {chanRows.map((c) => (
                                <BarRow key={c.channelKey}
                                    label={<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name={channelIcon(c.channelKey)} size={14} color={T.t3} />{c.channelName}</span> as any}
                                    display={`${c.customers.toLocaleString()} guest${c.customers === 1 ? "" : "s"}`}
                                    sub={money(c.avgOrderValue)}
                                    pct={(c.customers / maxChanCust) * 100}
                                    color={T.accent} />
                            ))}
                            {/* Two separate reasons the numbers don't tie out, both
                                deliberate: rows over-count multi-channel guests
                                (CUST-13.1), and most orders have no guest attached.
                                Stated rather than left to read as a miscount. */}
                            {chan && (
                                <div style={{ marginTop: 10, fontSize: 10.5, color: T.t3, display: "flex", alignItems: "flex-start", gap: 6 }}>
                                    <Icon name="info" size={13} color={T.t3} />
                                    <span>
                                        A guest using more than one channel is counted in each, so the rows
                                        total {chan.summedChannelCustomers.toLocaleString()} against {chan.distinctCustomers.toLocaleString()} actual guests.
                                        Average order value covers all {chan.totalOrders.toLocaleString()} orders
                                        {chan.unattributedOrders > 0 && <> — including {chan.unattributedOrders.toLocaleString()} not linked to a guest profile</>}.
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </SectionCard>

                {SHOW_ACQUISITION && (
                <SectionCard title="Acquisition Source" info={infoCopy("acquisition")} icon="travel_explore" subtitle="Where customers come from — tied to loyalty & value" style={{ flex: 1.3, minWidth: 0 }}>
                    {acquisition.error && !acquisition.loading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                            <Icon name="error" size={15} color={T.red} />
                            <span style={{ flex: 1 }}>Couldn't load acquisition sources. Please try again.</span>
                            <button onClick={acquisition.refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                        </div>
                    )}
                    {acquisition.loading && !acquisition.data && <Bone h={150} r={8} />}
                    {!acquisition.loading && !acquisition.error && acquisition.data && (
                    <>
                    {/* Said plainly rather than left for the operator to wonder at:
                        a table that is 100% Walk-in looks broken otherwise. */}
                    {acquisition.data.totalNewCustomers > 0 && acquisition.data.attributedCustomers === 0 && (
                        <div style={{ fontSize: 11.5, color: T.t3, background: T.bg, borderRadius: 6, padding: "7px 10px", marginBottom: 10, lineHeight: 1.5 }}>
                            <Icon name="info" size={12} color={T.t3} style={{ marginRight: 4, verticalAlign: "-1px" }} />
                            None of this period's {acquisition.data.totalNewCustomers} new customers carry an
                            attribution yet, so they all count as Walk-in / Unattributed. Marketing
                            sources appear here once attribution capture is live.
                        </div>
                    )}
                    {/* The API sends only the sources that brought in new customers in
                        the period (CUST-19.1, QA #19), so a period with none - e.g. Today
                        before any order - gets no rows, not "0 new" rows. */}
                    {acqRows.length === 0 ? (
                        <div style={{ padding: "22px 12px", textAlign: "center", color: T.t3, fontSize: 12.5 }}>
                            No new customers in this period
                        </div>
                    ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Source</th>
                                    <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleAcq("newCount")}>New {acqSort.key === "newCount" && <Icon name={acqSort.dir === "asc" ? "arrow_upward" : "arrow_downward"} size={11} color={T.accent} />}</th>
                                    <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleAcq("repeatRate")}>30-Day Repeat {acqSort.key === "repeatRate" && <Icon name={acqSort.dir === "asc" ? "arrow_upward" : "arrow_downward"} size={11} color={T.accent} />}</th>
                                    <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleAcq("avgClv")}>Avg CLV {acqSort.key === "avgClv" && <Icon name={acqSort.dir === "asc" ? "arrow_upward" : "arrow_downward"} size={11} color={T.accent} />}</th>
                                    <th style={thStyle}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {acqRows.map((a) => {
                                    const smallSample = a.smallSample;
                                    return (
                                        <tr key={a.key} onClick={() => openDrill({ key: `acq-${a.key}`, drill: "acquisition", sourceKey: a.key, badge: { label: a.source, color: T.blueDark }, headerStats: [{ label: "New (period)", value: String(a.newCount) }, { label: "30-Day Repeat", value: a.repeatRate === null ? "—" : `${a.repeatRate}%` }, { label: "Avg CLV", value: a.avgClv === null ? "—" : money(a.avgClv) }] })}
                                            style={{ borderBottom: `1px solid ${T.divider}`, cursor: "pointer" }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                            <td style={tdCell}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name={a.icon} size={14} color={T.t3} />{a.source}</span></td>
                                            <td style={{ ...tdCell, fontWeight: 600 }}>{a.newCount} new</td>
                                            {/* null means the source acquired nobody — undefined, not 0%. */}
                                            <td style={{ ...tdCell, color: T.t2 }}>{a.repeatRate === null ? "—" : `${a.repeatRate}%`}{/* TapTip, not `title`: a phone has no hover (QA #22). Its tap stops
                                                propagation, so it opens the note, not the row's drill. */}
                                            {smallSample && (
                                                <TapTip inline width={200} content={`Based on ${a.historical} customers — interpret with caution.`} style={{ marginLeft: 3, display: "inline-flex", verticalAlign: "middle" }}>
                                                    <Icon name="warning" size={12} color={T.orange} />
                                                </TapTip>
                                            )}</td>
                                            <td style={{ ...tdCell, fontWeight: 600 }}>{a.avgClv === null ? "—" : money(a.avgClv)}</td>
                                            <td style={{ ...tdCell, textAlign: "right" }}><Icon name="chevron_right" size={16} color={T.t3} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}
                    {/* QA #55: say what each column is built from. New and 30-day repeat are this
                        period's own new customers per source; Avg CLV and the drill cover everyone
                        the source ever brought in (CUST-19.1). The stamp KPI's 30-Day Return Rate uses a
                        different, lagged cohort (CUST-1.3), so the two will not match. */}
                    {acqRows.length > 0 && (
                        <div style={{ fontSize: 11, color: T.t3, marginTop: 10, lineHeight: 1.5, display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <Icon name="info" size={12} color={T.t3} style={{ marginTop: 2 }} />
                            <span>
                                <b>30-day repeat</b> — of this period's new customers from that source, the share who came back within 30 days.{" "}
                                <b>Avg CLV</b> — every customer the source has ever brought in, not just this period's. The 30-Day Return Rate card above
                                measures a different group (guests acquired 30 days earlier), so the two can differ.
                            </span>
                        </div>
                    )}
                    </>
                    )}
                </SectionCard>
                )}
            </div>

            {/* ═══ ISSUES & CHARGEBACKS (CUST-14 / 15 / 16) — hidden for now (SHOW_ISSUES) ═══ */}
            {SHOW_ISSUES && (
            <SectionCard title="Issues & Chargebacks" icon="report" subtitle="Complaints, cancellations & dispute signals">
                <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(4,1fr)" : "repeat(2,1fr)", gap: 10, marginBottom: 16 }}>
                    <IssueKpi label="Complaints" info={infoCopy("complaints")} value={String(COMPLAINTS.total.current)} delta={`${complaintDelta > 0 ? "+" : ""}${complaintDelta}`} positive={complaintDelta <= 0} />
                    <IssueKpi label="Complaint Rate" value={`${complaintRate}%`} delta="" positive />
                    <IssueKpi label="Cancelled Orders" info={infoCopy("cancellations")} value={String(CANCELLATIONS.orders.current)} delta={`${cancelDelta > 0 ? "+" : ""}${cancelDelta}`} positive={cancelDelta <= 0} />
                    <IssueKpi label="Cancellation Rate" value={`${cancelRate}%`} delta="" positive />
                </div>
                <div style={{ display: "flex", gap: isDesktop ? 20 : 12, flexDirection: isDesktop ? "row" : "column" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t2, marginBottom: 10 }}>Top Complaint Reasons</div>
                        {COMPLAINTS.reasons.map((r) => (
                            <BarRow key={r.reason} label={r.reason} display={`${r.count}`} sub={`${Math.round((r.count / COMPLAINTS.total.current) * 100)}%`} pct={(r.count / maxComplaint) * 100} color={T.red} />
                        ))}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t2, marginBottom: 10 }}>Most Cancelled Items</div>
                        {CANCELLATIONS.items.map((it) => (
                            <div key={it.item} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.divider}`, gap: 8 }}>
                                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                    <span style={{ minWidth: 26, textAlign: "center", fontSize: 11, fontWeight: 700, color: T.red, background: T.redSoft, borderRadius: 4, padding: "2px 6px" }}>{it.count}</span>
                                    <span style={{ minWidth: 0 }}>
                                        <span style={{ fontSize: 12.5, color: T.t1, fontWeight: 500 }}>{it.item}</span>
                                        <span style={{ display: "block", fontSize: 10.5, color: T.t3 }}>{it.reason}</span>
                                    </span>
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: T.t2, whiteSpace: "nowrap" }}>{money(it.count * it.unitPrice)} lost</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{ marginTop: 12, fontSize: 11, color: T.t3, display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon name="info" size={13} color={T.t3} />Repeat-disputer flags & per-customer dispute history appear on each customer's profile. Aggregate chargeback exposure lives in the Payments / EOD report.
                </div>
            </SectionCard>
            )}

            {/* ═══ CUSTOMER DIRECTORY (CUST-8.1) ═══ */}
            <CustomerDirectory scope={directoryScope} openProfile={openProfile} refreshKey={directoryRefreshKey} />

            {/* CUST-3.4 — cooldown confirm + failure toast */}
            {cooldownPrompt !== null && (
                <CooldownPrompt
                    minutesAgo={cooldownPrompt}
                    onConfirm={() => { setCooldownPrompt(null); handleRefresh(true); }}
                    onCancel={() => setCooldownPrompt(null)}
                />
            )}
            {toast && <Toast message={toast} />}
        </div>
    );
}

/**
 * CUST-12.1's delta: "+$3.40 with green arrow". An ABSOLUTE currency amount,
 * not a percentage like every other delta on this screen — so it is formatted
 * as money and never carries a % sign.
 *
 * `null` (either period had no orders) renders "—" rather than a rise out of
 * nothing. A delta resting on a handful of prior orders is flagged as shaky
 * instead of hidden: the number is real, its significance is not.
 */
/**
 * The Avg Spend card's comparison window in words — "Mon, Oct 5" for a day,
 * "Sep 29 – Oct 5" for a range (QA #27). Read from the response's
 * `priorStartDate` / `priorEndDate`, so it always names the days the delta was
 * really computed against. Parsed as local calendar dates: `new Date("2026-10-05")`
 * would be UTC midnight and show the previous day west of UTC.
 */
function comparisonDaysLabel(start: string, end: string): string {
    const parse = (iso: string) => {
        const [y, m, d] = iso.split("-").map(Number);
        return new Date(y, (m || 1) - 1, d || 1);
    };
    const s = parse(start);
    const e = parse(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "the comparison period";
    if (start === end) return s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const short = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${short(s)} – ${short(e)}`;
}

function SpendDelta({ delta, priorOrders }: { delta: number | null; priorOrders: number }) {
    const money = useMoney();
    if (delta === null) return <span style={{ color: T.t3 }}>—</span>;

    const up = delta > 0;
    const flat = delta === 0;
    const color = flat ? T.t3 : up ? T.green : T.red;
    const thin = priorOrders > 0 && priorOrders < 5;

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color, fontWeight: 600 }}>
            {!flat && <Icon name={up ? "arrow_upward" : "arrow_downward"} size={11} color={color} />}
            {up ? "+" : ""}{money(delta)}
            {/* TapTip, not `title`, so it opens on a phone too (QA #22). */}
            {thin && (
                <TapTip inline width={200} content={`Based on only ${priorOrders} prior order${priorOrders === 1 ? "" : "s"} — treat with caution.`} style={{ display: "inline-flex", verticalAlign: "middle" }}>
                    <Icon name="warning" size={10} color={T.orange} />
                </TapTip>
            )}
        </span>
    );
}

/**
 * Demo-mode control, and the warning that goes with it.
 *
 * Off, it is a single quiet link — this screen belongs to restaurant owners,
 * not only to us, so it must not look like a broken setting. On, it becomes an
 * unmissable amber bar: every number below it is invented, and the one genuinely
 * dangerous outcome here is someone screenshotting mock figures as real ones.
 */
function DemoModeBar({ demoMode, setDemoMode, isDesktop }: {
    demoMode: boolean;
    setDemoMode: (on: boolean) => void;
    isDesktop: boolean;
}) {
    // Renders nothing at all when the feature is off — no toggle, no banner.
    if (!enablePreviewWithSampleDataMode) return null;

    if (!demoMode) {
        return (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => setDemoMode(true)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "none", cursor: "pointer", color: T.t3, fontSize: 11, padding: "2px 4px", fontFamily: T.font }}>
                    <Icon name="science" size={13} color={T.t3} />
                    Preview with sample data
                </button>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(234,88,12,0.10)", border: "1px solid rgba(234,88,12,0.35)", flexWrap: "wrap" }}>
            <Icon name="science" size={17} color={T.orange} />
            <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.orange }}>Sample data — not real figures</div>
                <div style={{ fontSize: 11, color: T.t2, marginTop: 2 }}>
                    Every number on this screen is invented, for reviewing the layout and the
                    drill-downs while the endpoints are undeployed.
                    {isDesktop && " Nothing here reflects this outlet."}
                </div>
            </div>
            <button onClick={() => setDemoMode(false)}
                style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${T.orange}`, background: T.surface, color: T.orange, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: T.font, whiteSpace: "nowrap" }}>
                Back to live data
            </button>
        </div>
    );
}

function IssueKpi({ label, value, delta, positive, info }: any) {
    return (
        <div style={{ background: T.bg, border: `1px solid ${T.divider}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, display: "flex", alignItems: "center" }}>{label}{info && <InfoTip text={info} />}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.t1, marginTop: 4 }}>{value}</div>
            {delta && <div style={{ fontSize: 11, fontWeight: 600, color: positive ? T.green : T.red, marginTop: 2 }}>{delta}</div>}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-8.1 — CUSTOMER DIRECTORY (all customers, server-side paginated)
   Live on `/api/clickhouse/customer-reports/customer-directory-kpi`: search,
   sort and paging are resolved server-side over the whole directory, and only
   the current page is fetched and rendered.
   Each row carries its contacts both full and masked, so the column eye toggles
   switch between them without a second request — the reveal is a display state,
   not a query parameter. Exports go through the CUST-9.2 consent dialog.
   Row click still resolves against the mock profile data; the per-customer
   profile endpoint is not built yet, so a live row's click is currently inert.
   ═══════════════════════════════════════════════════════════════════════════ */
/** The report masks phones as "•••• •••• 4821" everywhere else; the endpoint
 *  returns the bare last 4 digits, so the display shape is rebuilt here. */
const maskedPhone = (last4: string | null) => (last4 ? `•••• •••• ${last4}` : "—");

/** Mirrors `MAX_DIRECTORY_PAGE_SIZE` on the backend: the export asks for the
 *  whole filtered set as a single page, and anything larger is a 400. */
const MAX_DIRECTORY_EXPORT_ROWS = 10000;

/**
 * CUST-9.1 / 9.2 — one customer list as an export table, shared by the
 * Customer Directory and every drill so the two files never disagree. The
 * whole filtered, sorted set comes back in ONE call to the endpoint the table
 * already reads (`page: 1`, `pageSize` up to the backend's 10 000 ceiling).
 *
 * `outletId` is set only for an "All outlets" export: that outlet's count is
 * unknown here, so it asks for the ceiling. The current outlet asks for exactly
 * the count its table is showing. Total Spend carries the currency on EVERY
 * cell (`formatMoneyForExport`, QA #28) rather than in the header: the
 * multi-outlet export points it at each outlet's own currency while that
 * outlet's rows are built, so an "All outlets" file mixing $ and ₹ outlets
 * labels every amount correctly - the Snapshot / Sales convention.
 */
async function buildCustomerListExport(
    query: Omit<CustomerDirectoryRequest, "page" | "pageSize">,
    knownTotal: number,
    consent: ContactConsent,
    outletId?: string,
) {
    const pageSize = outletId ? MAX_DIRECTORY_EXPORT_ROWS : Math.max(1, Math.min(knownTotal, MAX_DIRECTORY_EXPORT_ROWS));
    const all = await getCustomerDirectoryApi({ ...query, locationId: outletId ?? query.locationId, page: 1, pageSize });
    const include = consent === "include";
    return {
        headers: ["Rank", "Customer Name", "Visits", "Total Spend", "Last Visit", "Segment", "Primary Channel", "Email", "Phone"],
        rows: all.customers.map((c) => [
            c.rank, c.name ?? "—", c.visits, formatMoneyForExport(c.totalSpend),
            c.lastVisit ?? "—", c.segmentName, c.primaryChannel ?? "—",
            (include ? c.email : c.emailMasked) ?? "—",
            include ? (c.phone ?? "—") : maskedPhone(c.phoneLast4),
        ]),
    };
}

/** File-name-safe slug for an export's context ("At Risk" → "At_Risk"). */
const fileSlug = (s: string) => s.trim().replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "customers";
const todayStamp = () => new Date().toISOString().slice(0, 10);

function CustomerDirectory({ scope, openProfile, refreshKey = 0 }: {
    scope: DirectoryScope;
    openProfile: (id: string) => void;
    /** Bumped by Refresh Segments so the directory re-queries the rebuilt data. */
    refreshKey?: number;
}) {
    const isDesktop = useIsDesktop();
    const consent = useContactConsent();
    const dispatch = useAppDispatch();
    const tableRef = useRef<HTMLDivElement>(null);

    const { locationId, startDate, endDate, live, demo } = scope;
    const ready = demo || (!!locationId && !!startDate && !!endDate);
    // Demo pages resolve locally, so they never touch the slice.
    const [demoPage, setDemoPage] = useState<CustomerDirectoryResponse | null>(null);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(CUST_CONFIG.directoryDefaultPageSize);
    const [sort, setSort] = useState<{ key: CustomerDirectorySortKey; dir: "asc" | "desc" }>({ key: "totalSpend", dir: "desc" });
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    // Column contact reveal — session-scoped (resets on navigating away). Every
    // row arrives with BOTH the full and the masked contact, so this is a pure
    // display switch: unlike the mock it no longer re-queries, which is why it
    // is absent from the fetch below.
    const [reveal, setReveal] = useState({ email: false, phone: false });

    const liveData = useAppSelector((s) => s.customersReport.directory);
    const liveFetching = useAppSelector((s) => s.customersReport.directoryLoading);
    const liveError = useAppSelector((s) => s.customersReport.directoryError);

    /*
     * The slice holds whatever the LAST query left there - another period,
     * outlet, page or search - and the first render after a change happens
     * before the fetch effect has dispatched. So the stored page is only trusted
     * once the request for exactly this query has been sent; until it lands the
     * table shows shimmer, never the previous result (same rule as the drills,
     * QA #15).
     */
    const queryKey = JSON.stringify([locationId, live, startDate, endDate, page, pageSize, sort.key, sort.dir, search, refreshKey]);
    const [sentKey, setSentKey] = useState<string | null>(null);
    const awaiting = !demo && ready && sentKey !== queryKey;
    const pagesRef = useRef<{ key: string; pages: number }>({ key: "", pages: 1 });

    const fetching = demo ? demoPage === null : (liveFetching || awaiting);
    const data = demo ? demoPage : (fetching || !ready ? null : liveData);
    const error = demo || fetching ? null : liveError;

    // Debounce typing so each keystroke isn't a query.
    useEffect(() => {
        const id = window.setTimeout(() => setSearch(searchInput), 300);
        return () => window.clearTimeout(id);
    }, [searchInput]);

    // Any change to search, sort, page size, outlet or period resets to page 1 —
    // the page number is only ever preserved across Next/Previous.
    useEffect(() => { setPage(1); }, [search, sort.key, sort.dir, pageSize, locationId, startDate, endDate]);

    const refetch = useCallback(() => {
        if (demo || !ready) return;
        dispatch(fetchCustomerDirectoryRequest({
            locationId, ...sentDates(live, startDate, endDate),
            page, pageSize, sortBy: sort.key, sortOrder: sort.dir, search,
        }));
        setSentKey(queryKey);
    }, [demo, dispatch, ready, locationId, live, startDate, endDate, page, pageSize, sort.key, sort.dir, search, queryKey]);

    useEffect(() => { refetch(); }, [refetch]);

    /* Demo pages resolve locally, through the same paging/sort/search resolver
       the mock always used, so the table behaves as it will live. Cancellation
       belongs to the effect rather than to `refetch` — a cleanup returned from a
       callback the effect merely calls is dropped, which would let a stale page
       land after a newer one. */
    useEffect(() => {
        if (!demo) return;
        let cancelled = false;
        buildDemoDirectory({
            startDate: startDate || "2026-09-01", endDate: endDate || "2026-09-21",
            page, pageSize, sortBy: sort.key, sortOrder: sort.dir, search,
        }).then((res) => { if (!cancelled) setDemoPage(res); });
        return () => { cancelled = true; };
    }, [demo, startDate, endDate, page, pageSize, sort.key, sort.dir, search]);

    const goToPage = (n: number) => {
        setPage(n);
        // Return the user to the top of the table, not wherever they were scrolled.
        tableRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    };

    const toggleSort = (key: CustomerDirectorySortKey) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

    const loading = fetching || (!data && !error && ready);

    const total = data?.totalCustomers ?? 0;
    // While a page loads `data` is null, so the page count is carried over from
    // the last loaded page of the SAME query (outlet, period, size, sort, search)
    // - never from another one.
    const pagesKey = JSON.stringify([locationId, live, startDate, endDate, pageSize, sort.key, sort.dir, search]);
    if (data) pagesRef.current = { key: pagesKey, pages: Math.max(1, data.totalPages) };
    const totalPages = pagesRef.current.key === pagesKey ? pagesRef.current.pages : 1;
    const showingFrom = data?.showingFrom ?? 0;
    const showingTo = data?.showingTo ?? 0;
    // The endpoint's count is exact by construction, so the mock's "~N" estimate
    // marker has nothing left to represent.
    const totalLabel = total.toLocaleString();
    const subtitle = error
        ? "Couldn't load the directory"
        : total === 0
            ? (loading ? "Loading…" : search ? `No matches for "${search}"` : "No customers")
            : `Showing ${showingFrom.toLocaleString()}–${showingTo.toLocaleString()} of ${totalLabel}${search ? ` matching "${search}"` : ""}`;

    /* CUST-9.2 — the export is this same endpoint with page 1 and pageSize set
       to the count the table just read, so the file holds the WHOLE filtered,
       sorted set rather than the page on screen. It goes through the shared
       export control like every other report (QA #11): CSV / Excel / PDF, and
       for multi-outlet users the This outlet / All outlets scope - then the
       CUST-9 contact-consent prompt, asked once per export. */
    const directoryQuery = {
        locationId, ...sentDates(live, startDate, endDate),
        sortBy: sort.key, sortOrder: sort.dir, search,
    };
    return (
        <SectionCard title="Customer Directory" icon="groups" subtitle={subtitle}
            right={
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", width: isDesktop ? undefined : "100%" }}>
                    <CustomerSearchBox value={searchInput} onChange={setSearchInput} />
                    {/* No "All" option: the endpoint takes a real page size, and the
                        export — not a 10k-row render — is how you get everything. */}
                    <PageSizeSelect value={pageSize} onChange={setPageSize} options={CUST_CONFIG.directoryPageSizes} allowAll={false} />
                    <ReportSectionExportButton
                        title="Customer Directory"
                        filename={`customers${search ? `_${fileSlug(search)}` : ""}_${todayStamp()}`}
                        dateRangeLabel={scope.periodLabel}
                        disabled={!ready || total === 0 || demo}
                        prepare={consent.ask}
                        buildExport={(_format, opts) => buildCustomerListExport(
                            directoryQuery, total, opts?.prepared as ContactConsent, opts?.locationId,
                        )}
                    />
                    {consent.dialog}
                </div>
            }
        >
            <div ref={tableRef} />

            {error && !fetching ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                    <Icon name="error" size={15} color={T.red} />
                    <span style={{ flex: 1 }}>Couldn't load the customer directory. Please try again.</span>
                    <button onClick={refetch} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                </div>
            ) : total === 0 && !loading ? (
                <div style={{ padding: "32px 16px", textAlign: "center", color: T.t3, fontSize: 13 }}>
                    {search ? `No customers found matching "${search}"` : "No customers ordered here in this period"}
                    {search && <div><span onClick={() => { setSearchInput(""); setSearch(""); }} style={{ color: T.accent, cursor: "pointer", fontWeight: 600, fontSize: 12.5 }}>Clear Search</span></div>}
                </div>
            ) : (
                <>
                    <CustomerRowsTable
                        rows={data?.customers ?? []}
                        loading={loading}
                        skeletonRows={DRILL_SKELETON_ROWS}
                        reveal={reveal}
                        onToggleReveal={(col) => setReveal((r) => ({ ...r, [col]: !r[col] }))}
                        sortKey={sort.key}
                        sortDir={sort.dir}
                        onToggleSort={toggleSort}
                        onRowClick={openProfile}
                    />

                    {/* Pagination bar — First / Prev / indicator / Next / Last */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 11.5, color: T.t3 }}>
                            {loading ? "" : `Showing ${showingFrom.toLocaleString()}–${showingTo.toLocaleString()} of ${totalLabel}. Use pagination or export to see more.`}
                        </div>
                        <PaginationBar page={page} totalPages={totalPages} onChange={goToPage} />
                    </div>
                </>
            )}
        </SectionCard>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-5.1 — SHARED CUSTOMER ROWS
   ---------------------------------------------------------------------------
   The story calls this "a reusable table component used across all drill-down
   entry points", and notes the Customer Directory "uses the same masked-contact
   and row conventions". So the Directory and every drill render through here:
   same columns in the same order, same eye toggles, same masking.

   Presentation only. Fetching, paging and sorting STATE stay with each parent,
   because those genuinely differ — the Directory owns a redux slice and an
   export, a drill owns its own target. What must not differ is how a customer
   row looks, and that is what lives here.
   ═══════════════════════════════════════════════════════════════════════════ */
function CustomerRowsTable({ rows, loading, skeletonRows, reveal, onToggleReveal, sortKey, sortDir, onToggleSort, onRowClick }: {
    rows: CustomerDirectoryRow[];
    loading: boolean;
    /**
     * When set, loading shows this many shimmer rows instead of dimming the
     * rows already on screen. The Directory and every drill set it: a kept
     * page may belong to another drill or period (QA #15).
     */
    skeletonRows?: number;
    reveal: { email: boolean; phone: boolean };
    onToggleReveal: (col: "email" | "phone") => void;
    sortKey: CustomerDirectorySortKey;
    sortDir: "asc" | "desc";
    onToggleSort: (key: CustomerDirectorySortKey) => void;
    onRowClick: (customerId: string) => void;
}) {
    const money = useMoney();

    const sortableTh = (label: string, key: CustomerDirectorySortKey) => (
        <th key={label} onClick={() => onToggleSort(key)} style={{ ...thStyle, cursor: "pointer" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {label}
                {sortKey === key && <Icon name={sortDir === "asc" ? "arrow_upward" : "arrow_downward"} size={12} color={T.accent} />}
            </span>
        </th>
    );

    // CUST-5.1: one click unmasks the whole column, and the icon flips state.
    const contactTh = (label: string, col: "email" | "phone") => (
        <th key={label} style={thStyle}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {label}
                <Icon
                    name={reveal[col] ? "visibility" : "visibility_off"}
                    size={14}
                    color={reveal[col] ? T.accent : T.t3}
                    title={reveal[col] ? `Hide ${label.toLowerCase()}` : `Reveal ${label.toLowerCase()}`}
                    onClick={(e: any) => { e.stopPropagation(); onToggleReveal(col); }}
                />
            </span>
        </th>
    );

    const shimmer = loading && !!skeletonRows;

    return (
        <div style={{ overflowX: "auto", opacity: loading && !shimmer ? 0.55 : 1, transition: "opacity 0.15s" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                <thead>
                    <tr>
                        {/* QA #56: "Rank", like the export's first column (CUST-9.1). CUST-8.1 writes
                            the on-screen column as "#" and CUST-9.1 writes the export's as "Rank" for the
                            same thing, so the two screens disagreed; one word on both. This is the shared
                            table, so the Directory and every drill read the same. */}
                        <th style={thStyle}>Rank</th>
                        {sortableTh("Customer", "name")}
                        {sortableTh("Visits", "visits")}
                        {sortableTh("Total Spend", "totalSpend")}
                        {sortableTh("Last Visit", "lastVisit")}
                        {sortableTh("Segment", "segment")}
                        {sortableTh("Primary Channel", "primaryChannel")}
                        {contactTh("Email", "email")}
                        {contactTh("Phone", "phone")}
                    </tr>
                </thead>
                <tbody>
                    {shimmer && Array.from({ length: skeletonRows! }).map((_, i) => (
                        <tr key={`skeleton-${i}`} style={{ borderBottom: `1px solid ${T.divider}` }}>
                            <td style={tdCell}><Bone w={26} h={22} r={4} /></td>
                            <td style={tdCell}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <Bone w={26} h={26} r={13} /><Bone w={110} h={12} r={4} />
                                </div>
                            </td>
                            <td style={tdCell}><Bone w={24} h={12} r={4} /></td>
                            <td style={tdCell}><Bone w={56} h={12} r={4} /></td>
                            <td style={tdCell}><Bone w={76} h={12} r={4} /></td>
                            <td style={tdCell}><Bone w={72} h={18} r={9} /></td>
                            <td style={tdCell}><Bone w={70} h={12} r={4} /></td>
                            <td style={tdCell}><Bone w={120} h={12} r={4} /></td>
                            <td style={tdCell}><Bone w={84} h={12} r={4} /></td>
                        </tr>
                    ))}
                    {!shimmer && rows.map((row) => {
                        // Nullable columns render an em dash; the contact columns pick
                        // whichever of the two forms the row already carries.
                        const name = row.name ?? "—";
                        const channel = row.primaryChannel ?? "—";
                        const email = (reveal.email ? row.email : row.emailMasked) ?? "—";
                        const phone = reveal.phone ? (row.phone ?? "—") : maskedPhone(row.phoneLast4);
                        return (
                            <tr key={row.customerId} onClick={() => onRowClick(row.customerId)}
                                style={{ borderBottom: `1px solid ${T.divider}`, cursor: "pointer" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                <td style={tdCell}>
                                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 26, height: 22, padding: "0 5px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: row.rank === 1 ? T.accentSoft : T.hover, color: row.rank === 1 ? T.accent : T.t3 }}>{row.rank}</span>
                                </td>
                                <td style={tdCell}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <Avatar name={name} color={segmentColor(row.segmentName)} size={26} />
                                        <span style={{ fontSize: 12.5, fontWeight: 500, color: T.t1, whiteSpace: "nowrap" }}>{name}</span>
                                    </div>
                                </td>
                                <td style={tdCell}>{row.visits}</td>
                                {/* Exact to the cent - a whole-dollar figure hid real spend ($26.84 read as $27). */}
                                <td style={{ ...tdCell, fontWeight: 600 }}>{money(row.totalSpend, { decimals: true })}</td>
                                <td style={{ ...tdCell, color: T.t2 }}>{row.lastVisit ?? "—"}</td>
                                <td style={tdCell}><SegmentBadge label={row.segmentName} color={segmentColor(row.segmentName)} /></td>
                                <td style={{ ...tdCell, color: T.t2 }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        <Icon name={channelIcon(row.primaryChannel)} size={14} color={T.t3} />{channel}
                                    </span>
                                </td>
                                <td style={{ ...tdCell, color: T.t2, fontVariantNumeric: "tabular-nums" }}>{email}</td>
                                <td style={{ ...tdCell, color: T.t2, fontVariantNumeric: "tabular-nums" }}>{phone}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL VIEW (CUST-6.1 / 6.2 / 6.3 / CUST-2.2)
   ---------------------------------------------------------------------------
   Live on the two drill endpoints. `DrillSpec` carries a TARGET, not data: the
   header and the table each fetch for themselves, from endpoints that share one
   definition of the customer set, so CUST-6.1's "title — 438 AND the table
   filtered to…" can never disagree.

   Acquisition Source (CUST-19.2) has no endpoint, so it still passes a mock
   list and takes the legacy path below.
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillView({ drill, scope, demoMode, onBack, openProfile, openExport }: {
    drill: DrillSpec;
    scope: DirectoryScope;
    demoMode: boolean;
    onBack: () => void;
    openProfile: (id: string) => void;
    openExport: (spec: ExportFlowSpec) => void;
}) {
    const dispatch = useAppDispatch();
    const money = useMoney();
    const isDesktop = useIsDesktop();
    const tableRef = useRef<HTMLDivElement>(null);
    const consent = useContactConsent();

    const { locationId, startDate, endDate, live } = scope;
    const ready = demoMode || (!!locationId && !!startDate && !!endDate);
    // The card that opened the drill may already hold the header figures, in
    // which case fetching them again would be a wasted call AND a second set of
    // numbers for the same thing.
    const statsFromCard = drill.headerStats;

    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<{ key: CustomerDirectorySortKey; dir: "asc" | "desc" }>({ key: "totalSpend", dir: "desc" });
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    // Session-scoped per CUST-5.1, and a pure display switch: each row carries
    // both the full and the masked contact.
    const [reveal, setReveal] = useState({ email: false, phone: false });
    const [demoStats, setDemoStats] = useState<DrilldownStatsResponse | null>(null);
    const [demoRows, setDemoRows] = useState<DrilldownCustomersResponse | null>(null);

    const liveStats = useAppSelector((s) => s.customersReport.drilldownStats);
    const liveStatsLoading = useAppSelector((s) => s.customersReport.drilldownStatsLoading);
    const liveStatsError = useAppSelector((s) => s.customersReport.drilldownStatsError);
    const liveRows = useAppSelector((s) => s.customersReport.drilldownCustomers);
    const liveRowsLoading = useAppSelector((s) => s.customersReport.drilldownCustomersLoading);
    const liveRowsError = useAppSelector((s) => s.customersReport.drilldownCustomersError);

    const target = useMemo(() => ({
        locationId, ...sentDates(live, startDate, endDate),
        // Both keys travel: `segment` needs segmentKey, `acquisition` needs
        // sourceKey, and the endpoint rejects a drill whose key is missing.
        drill: drill.drill, segmentKey: drill.segmentKey, sourceKey: drill.sourceKey,
    }), [locationId, live, startDate, endDate, drill.drill, drill.segmentKey, drill.sourceKey]);

    /*
     * Both drill slices are shared by every drill, so until THIS view's own
     * request is in flight the store still holds whatever the last drill (or the
     * last page of this one) left there - and the first render happens before
     * the fetch effect has even dispatched. Each slice is therefore only trusted
     * once the request for exactly this target / page / sort / search has been
     * sent; until then it reads as loading, so the view shows shimmer and never
     * another drill's header or customers (QA #15).
     */
    const statsKey = JSON.stringify(target);
    const rowsKey = JSON.stringify([target, page, sort.key, sort.dir, search]);
    const [sentStatsKey, setSentStatsKey] = useState<string | null>(null);
    const [sentRowsKey, setSentRowsKey] = useState<string | null>(null);
    const pagesRef = useRef<{ key: string; pages: number }>({ key: "", pages: 1 });
    const awaitingStats = !demoMode && !statsFromCard && ready && sentStatsKey !== statsKey;
    const awaitingRows = !demoMode && ready && sentRowsKey !== rowsKey;

    const statsLoading = demoMode ? demoStats === null : (liveStatsLoading || awaitingStats);
    // Without a ready target nothing is requested, so nothing in the store is ours.
    const stats = demoMode ? demoStats : (statsLoading || !ready ? null : liveStats);
    const statsError = demoMode || statsLoading || !ready ? null : liveStatsError;
    const rowsLoading = demoMode ? demoRows === null : (liveRowsLoading || awaitingRows);
    const rowsData = demoMode ? demoRows : (rowsLoading || !ready ? null : liveRows);
    const rowsError = demoMode || rowsLoading || !ready ? null : liveRowsError;

    /*
     * When the card supplies the header figures the stats endpoint is never
     * called, so `liveStats*` still holds whatever the PREVIOUS drill left in
     * the store. Reading it directly would show that drill's error (behind a
     * Retry that deliberately does nothing), its loading skeletons, or its
     * segment mini-bar. These three are what the header actually renders from.
     */
    const statsPending = statsFromCard ? false : statsLoading;
    const statsProblem = statsFromCard ? null : statsError;
    const distribution = statsFromCard ? [] : (stats?.segmentDistribution ?? []);

    // Debounced, like the directory — one query per pause, not per keystroke.
    useEffect(() => {
        const id = window.setTimeout(() => setSearch(searchInput), 300);
        return () => window.clearTimeout(id);
    }, [searchInput]);

    useEffect(() => { setPage(1); }, [search, sort.key, sort.dir, drill.key]);

    const refetchStats = useCallback(() => {
        // `stamp-kpi-drilldowns` has no sourceKey parameter, so an acquisition
        // drill would match nothing there and come back "Source — 0 Customers".
        // When the card already supplied the figures there is nothing to ask it
        // for anyway.
        if (!ready || demoMode || statsFromCard) return;
        dispatch(fetchDrilldownStatsRequest(target));
        setSentStatsKey(statsKey);
    }, [dispatch, ready, demoMode, statsFromCard, target, statsKey]);

    const refetchRows = useCallback(() => {
        if (!ready || demoMode) return;
        dispatch(fetchDrilldownCustomersRequest({
            ...target, page, pageSize: DRILL_PAGE_SIZE,
            sortBy: sort.key, sortOrder: sort.dir, search,
        }));
        setSentRowsKey(rowsKey);
    }, [dispatch, ready, demoMode, target, page, sort.key, sort.dir, search, rowsKey]);

    useEffect(() => { refetchStats(); }, [refetchStats]);
    useEffect(() => { refetchRows(); }, [refetchRows]);

    // Demo resolves locally; cancellation lives in the effect so a stale page
    // cannot land after a newer one.
    useEffect(() => {
        // Skipped when the card supplied the figures - there is nothing to build.
        if (!demoMode || statsFromCard) return;
        setDemoStats(buildDemoDrilldownStats(drill.drill, drill.segmentKey));
    }, [demoMode, statsFromCard, drill.drill, drill.segmentKey]);

    useEffect(() => {
        if (!demoMode) return;
        setDemoRows(buildDemoDrilldownCustomers({
            ...target, page, pageSize: DRILL_PAGE_SIZE,
            sortBy: sort.key, sortOrder: sort.dir, search,
        }));
    }, [demoMode, target, page, sort.key, sort.dir, search]);

    const goToPage = (n: number) => {
        setPage(n);
        tableRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    };

    const toggleSort = (key: CustomerDirectorySortKey) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

    /* ── Header stats, per CUST-6.1 / 6.2 / 6.3 / 2.2 ── */
    const headerStats: { label: string; value: string }[] = [];
    if (statsFromCard) {
        headerStats.push(...statsFromCard);
    } else if (stats) {
        const n = (v: number | null, fmt: (x: number) => string) => (v === null ? "—" : fmt(v));
        headerStats.push({ label: "Customers", value: stats.customerCount.toLocaleString() });
        if (drill.drill === "new") {
            headerStats.push({ label: "Avg First-Visit Spend", value: n(stats.avgFirstVisitSpend, money) });
            headerStats.push({ label: "Top Channel", value: stats.topChannel?.channelName ?? "—" });
        } else if (drill.drill === "returning") {
            // CUST-6.2: "avg spend per returning customer" - spend ÷ customers, not
            // ÷ orders. Falls back to the per-order value only until the backend
            // that sends `avgSpendPerCustomer` is deployed.
            headerStats.push({ label: "Avg Spend", value: n(stats.avgSpendPerCustomer ?? stats.avgOrderValue, money) });
        } else {
            headerStats.push({ label: "Avg CLV", value: n(stats.avgClv, money) });
            headerStats.push({ label: "Avg Visits", value: n(stats.avgVisits, (x) => String(x)) });
            // CUST-2.2 / 6.3: At Risk and segment customers (Slipping Away,
            // Lost, ...) often have not ordered in the selected period, so a
            // period-scoped average read "—". Both use the ALL-TIME average
            // order value, matching the table's lifetime Total Spend.
            const avgSpend = stats.avgLifetimeOrderValue ?? stats.avgOrderValue;
            headerStats.push({ label: "Avg Spend", value: n(avgSpend, money) });
        }
    }

    const rows = rowsData?.customers ?? [];
    const total = rowsData?.totalCustomers ?? 0;
    // While a page loads `rowsData` is null, so the page count is carried over
    // from this drill's last loaded page - but never from another drill's.
    if (rowsData) pagesRef.current = { key: statsKey, pages: Math.max(1, rowsData.totalPages) };
    const totalPages = pagesRef.current.key === statsKey ? pagesRef.current.pages : 1;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <DrillBackButton onBack={onBack} />

            <SectionCard>
                {statsProblem ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.red }}>
                        <Icon name="error" size={15} color={T.red} />
                        <span style={{ flex: 1 }}>Couldn't load this drill-down. {statsProblem}</span>
                        <button onClick={refetchStats} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                    </div>
                ) : (
                    <>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            {drill.badge && <SegmentBadge label={drill.badge.label} color={drill.badge.color} />}
                            <div style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>
                                {/* The rows response carries the same server-built
                                    title, so a card-supplied header still gets
                                    the real name and count. */}
                                {statsPending || rowsLoading
                                    ? <Bone w={220} h={20} r={5} />
                                    : (statsFromCard ? rowsData?.title : stats?.title)}
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
                            {statsPending
                                ? Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i}><Bone w={92} h={10} r={3} /><div style={{ height: 6 }} /><Bone w={64} h={16} r={4} /></div>
                                ))
                                : headerStats.map((s) => <DrillStat key={s.label} label={s.label} value={s.value} />)}
                        </div>
                        {/* CUST-6.2's segment distribution. Shown for every drill except
                            a segment one, where a single 100% bar says nothing. */}
                        {!statsPending && drill.drill !== "segment" && distribution.length > 1 && (
                            <div style={{ marginTop: 14 }}>
                                <div style={{ fontSize: 10, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 6 }}>Segment mix</div>
                                <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: T.hover }}>
                                    {distribution.map((s) => (
                                        <div key={s.key} title={`${s.name}: ${s.customers.toLocaleString()} (${s.percentOfDrilldown}%)`}
                                            style={{ width: `${s.percentOfDrilldown}%`, background: segmentColor(s.name) }} />
                                    ))}
                                </div>
                                <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
                                    {distribution.slice(0, 5).map((s) => (
                                        <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: T.t3 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: 2, background: segmentColor(s.name) }} />
                                            {s.name} {s.percentOfDrilldown}%
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </SectionCard>

            <SectionCard
                title="Customers"
                subtitle={rowsData && total > 0
                    ? `Showing ${rowsData.showingFrom.toLocaleString()}–${rowsData.showingTo.toLocaleString()} of ${total.toLocaleString()}${search ? ` matching "${search}"` : ""}`
                    : undefined}
                right={
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: isDesktop ? "nowrap" : "wrap", width: isDesktop ? undefined : "100%" }}>
                        <CustomerSearchBox value={searchInput} onChange={setSearchInput} />
                        {/* CUST-9.1 — every drill exports its WHOLE list (not the page on
                            screen) through the shared control: format, outlet scope, then
                            the contact-consent prompt (QA #12). */}
                        <ReportSectionExportButton
                            title={rowsData?.title ?? `${drill.badge?.label ?? "Customer"} Customers`}
                            filename={`${fileSlug(drill.badge?.label ?? "customers")}_customers_${todayStamp()}`}
                            dateRangeLabel={scope.periodLabel}
                            disabled={demoMode || !ready || rowsLoading || total === 0}
                            prepare={consent.ask}
                            buildExport={(_format, opts) => buildCustomerListExport(
                                { ...target, sortBy: sort.key, sortOrder: sort.dir, search },
                                total, opts?.prepared as ContactConsent, opts?.locationId,
                            )}
                        />
                        {consent.dialog}
                    </div>
                }
            >
                <div ref={tableRef} />
                {rowsError && !rowsLoading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: T.redSoft, color: T.red, fontSize: 12 }}>
                        <Icon name="error" size={15} color={T.red} />
                        <span style={{ flex: 1 }}>Couldn't load the customer list. Please try again.</span>
                        <button onClick={refetchRows} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
                    </div>
                ) : total === 0 && !rowsLoading ? (
                    <div style={{ padding: "32px 16px", textAlign: "center", color: T.t3, fontSize: 13 }}>
                        {search
                            ? `No customers found matching "${search}"`
                            : ready
                                ? "No customers in this drill-down yet."
                                : "Couldn't load this drill-down — no outlet or date range selected."}
                    </div>
                ) : (
                    <>
                        <CustomerRowsTable
                            rows={rows}
                            loading={rowsLoading}
                            skeletonRows={DRILL_SKELETON_ROWS}
                            reveal={reveal}
                            onToggleReveal={(col) => setReveal((r) => ({ ...r, [col]: !r[col] }))}
                            sortKey={sort.key}
                            sortDir={sort.dir}
                            onToggleSort={toggleSort}
                            onRowClick={(id) => openProfile(id)}
                        />
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                            <div style={{ fontSize: 11.5, color: T.t3 }}>
                                Visits and spend are lifetime at this outlet, not just this period.
                            </div>
                            <PaginationBar page={page} totalPages={totalPages} onChange={goToPage} />
                        </div>
                    </>
                )}
            </SectionCard>
        </div>
    );
}

/**
 * Owns the fetch, so the hook is not called from `CustomersBody` - which also
 * renders the dashboard and the drill, and would otherwise run a customer
 * request on every view.
 */
function LiveProfileRoute({ customerId, scope, demoMode, onBack, sync }: {
    customerId: string;
    scope: { locationId: string; startDate: string; endDate: string; live: boolean };
    demoMode: boolean;
    onBack: () => void;
    sync: any;
}) {
    const { data, loading, error, setPage, sort, toggleSort, pageSize, setPageSize, searchInput, setSearchInput, search } = useCustomerDetails(customerId, scope, demoMode);

    /* The Order Log export (QA #12) holds the customer's WHOLE log, not the page
       on screen: the details endpoint pages at most 500 orders, so it walks the
       pages in order. Same outlet and as-of date as the profile itself. */
    const fetchAllOrders = useCallback(async () => {
        const request = (page: number) => getCustomerDetailsApi({
            locationId: scope.locationId, customerId,
            ...sentDates(scope.live, scope.startDate, scope.endDate),
            page, pageSize: ORDER_LOG_EXPORT_PAGE_SIZE,
            // The file follows the table: same order as on screen.
            sortBy: sort.key, sortOrder: sort.dir,
        });
        const first = await request(1);
        const orders = [...first.orderLog.content];
        for (let page = 2; page <= first.orderLog.totalPages; page++) {
            orders.push(...(await request(page)).orderLog.content);
        }
        return orders;
    }, [customerId, scope.locationId, scope.live, scope.startDate, scope.endDate, sort.key, sort.dir]);

    /* CUST-7.2 Visit Timeline (QA #13): the same order log, 10 at a time and
       always newest first - its own fetch, so it is unaffected by the Order
       Log's page and sort. */
    const fetchVisitPage = useCallback(async (page: number) => (await getCustomerDetailsApi({
        locationId: scope.locationId, customerId,
        ...sentDates(scope.live, scope.startDate, scope.endDate),
        page, pageSize: VISIT_TIMELINE_PAGE_SIZE, sortBy: "date", sortOrder: "desc",
    })).orderLog, [customerId, scope.locationId, scope.live, scope.startDate, scope.endDate]);

    return (
        <LiveCustomerProfile
            data={data}
            loading={loading}
            error={error}
            onBack={onBack}
            onPage={setPage}
            fetchAllOrders={demoMode ? undefined : fetchAllOrders}
            orderSort={sort}
            onOrderSort={toggleSort}
            orderPageSize={pageSize}
            onOrderPageSize={demoMode ? undefined : setPageSize}
            orderSearchInput={searchInput}
            onOrderSearchInput={demoMode ? undefined : setSearchInput}
            orderSearch={search}
            fetchVisitPage={demoMode ? undefined : fetchVisitPage}
            // Gates the RFM panel (CUST-17) - segment-derived, so it follows the
            // same refresh state as the Segment Breakdown.
            segmentsComputed={!sync.neverComputed}
        />
    );
}

function DrillBackButton({ onBack }: { onBack: () => void }) {
    return (
        <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", color: T.t2, fontSize: 13, fontWeight: 600, padding: 0, alignSelf: "flex-start" }}>
            <Icon name="arrow_back" size={18} color={T.t2} /> Back
        </button>
    );
}

function DrillStat({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div style={{ fontSize: 10.5, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.t1, marginTop: 2 }}>{value}</div>
        </div>
    );
}

const thStyle: React.CSSProperties = { fontSize: 10, fontWeight: 600, color: T.t3, textAlign: "left", padding: "8px 12px", textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap", borderBottom: `1px solid ${T.divider}` };
const tdCell: React.CSSProperties = { padding: "10px 12px", fontSize: 12.5, color: T.t1, whiteSpace: "nowrap" };

/* ═══════════════════════════════════════════════════════════════════════════
   BODY — routes dashboard / drill / profile
   ═══════════════════════════════════════════════════════════════════════════ */
function CustomersBody({ view, stampKpis, segments, channels, cohorts, segmentSpend, acquisition, directoryScope, demoMode, setDemoMode, openDrill, openProfile, openExport, back, sync }: any) {
    if (view.kind === "profile") {
        // Demo mode keeps the mock profile: it carries loyalty and modifier
        // detail the API has no source for, and demo exists to show the fuller
        // layout. A live row goes to the endpoint.
        if (demoMode) {
            const c = getCustomerById(view.customerId);
            if (c) {
                return <CustomerProfile customer={c} onBack={back} onOpenLoyalty={(cust) => openProfile.__loyalty?.(cust)} segmentsComputed />;
            }
        }
        return <LiveProfileRoute customerId={view.customerId} scope={directoryScope} demoMode={demoMode} onBack={back} sync={sync} />;
    }
    if (view.kind === "drill") {
        return <DrillView drill={view.drill} scope={directoryScope} demoMode={demoMode} onBack={back} openProfile={openProfile} openExport={openExport} />;
    }
    return <Dashboard stampKpis={stampKpis} segments={segments} channels={channels} cohorts={cohorts} segmentSpend={segmentSpend} acquisition={acquisition} directoryScope={directoryScope} demoMode={demoMode} setDemoMode={setDemoMode} openDrill={openDrill} openProfile={openProfile} openExport={openExport} sync={sync} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CustomersReport() {
    const isDesktop = useIsDesktop();
    useSwipeBack();
    const navigate = useNavigate();
    const { selectedBranch } = useBranch();
    // No excludeTokens: the KPI row resolves every token through the shared
    // `resolveTokenRange`, so a carried Last 3/6 Months or Last Year is fetched as shown.
    // No excludeToday either: CUST-10.1 lists Today on desktop and mobile, so the
    // calendar runs to today and a Today carried in from another report is kept.
    // Still opens on Yesterday, the last finalised day (5 AM anchor).
    const [datePeriod, setDatePeriod] = useSharedReportDateFilter("Yesterday");
    const locationId = String(selectedBranch?.id ?? selectedBranch?.locationId ?? "");
    const [demoMode, setDemoMode] = useDemoMode();
    const stampKpis = useCustomerStampKpis(locationId, datePeriod, demoMode);
    const segments = useCustomerSegments(locationId, demoMode);
    const channels = useChannelPerformance(locationId, datePeriod, demoMode);
    const acquisition = useAcquisitionSources(locationId, datePeriod, demoMode);
    // Read here rather than in the shells: they are two branches of one
    // screen, and the crumb must be identical on both.
    const profileName = useAppSelector((s) => s.customersReport.details?.profile.name ?? null);
    const cohorts = useCohortRetention(locationId, datePeriod, demoMode);
    const segmentSpend = useSegmentSpend(locationId, datePeriod, demoMode);
    // Resolved once here rather than inside the table, so a re-render from
    // typing in the directory's search box can't produce a new object identity
    // and re-trigger its fetch effect.
    const period = useRequestPeriod(datePeriod);
    const directoryScope = useMemo<DirectoryScope>(() => ({
        locationId, startDate: period.startDate, endDate: period.endDate, live: period.live, demo: demoMode,
        periodLabel: exportPeriodFor(datePeriod, period)?.label ?? datePeriod,
    }), [locationId, period, demoMode, datePeriod]);

    const [view, setView] = useState<View>({ kind: "dashboard" });
    const [exportSpec, setExportSpec] = useState<ExportFlowSpec | null>(null);

    const openDrill = useCallback((drill: DrillSpec) => setView((v) => ({ kind: "drill", drill, from: v })), []);
    const openProfile: any = useCallback((id: string) => setView((v) => ({ kind: "profile", customerId: id, from: v })), []);
    openProfile.__loyalty = (c: CustomerRecord) => navigate("/reports/loyalty", { state: { memberId: c.loyalty.memberId, memberName: c.name } });
    const openExport = useCallback((spec: ExportFlowSpec) => setExportSpec(spec), []);
    const back = useCallback(() => setView((v) => (v.kind === "dashboard" ? v : v.from)), []);

    // Reset to dashboard when the location changes so drills don't show stale data.
    useEffect(() => { setView({ kind: "dashboard" }); }, [selectedBranch?.id, selectedBranch?.locationId]);

    // Android back / swipe closes drill & profile before leaving the report.
    useDrillHistoryBack(view.kind !== "dashboard", back);

    // CUST-3.4 — sync status is scoped to the tenant. No client/tenant id exists
    // in the app yet, so the selected location stands in for it; this becomes the
    // real `client_id` when the segment_sync_status endpoint lands.
    const clientId = String(selectedBranch?.id ?? selectedBranch?.locationId ?? "default");
    const sync = useSegmentSync(clientId, "current-user");

    const crumb = useMemo(() => crumbFor(view, profileName), [view, profileName]);

    const bodyProps = { view, stampKpis, segments, channels, cohorts, segmentSpend, acquisition, directoryScope, demoMode, setDemoMode, openDrill, openProfile, openExport, back, sync };
    // A drill-down or customer page keeps the dates it was opened with, so the
    // date control is hidden there - same rule as Snapshot, Sales, Labor, EOD,
    // Check-in and Loyalty (`dateFilters: isDrill ? undefined : ...`).
    const inDrill = view.kind !== "dashboard";

    if (isDesktop) return <DesktopView crumb={crumb} inDrill={inDrill} datePeriod={datePeriod} setDatePeriod={setDatePeriod} bodyProps={bodyProps} exportSpec={exportSpec} closeExport={() => setExportSpec(null)} />;
    return <MobileView crumb={crumb} inDrill={inDrill} datePeriod={datePeriod} setDatePeriod={setDatePeriod} bodyProps={bodyProps}
        onRefresh={() => { stampKpis.refetch(); segments.refetch(); channels.refetch(); cohorts.refetch(); segmentSpend.refetch(); acquisition.refetch(); }}
        exportSpec={exportSpec} closeExport={() => setExportSpec(null)} />;
}

/* ─── Desktop shell ───────────────────────────────────────────────────────── */
function DesktopView({ crumb, inDrill, datePeriod, setDatePeriod, bodyProps, exportSpec, closeExport }: any) {
    const setTopBarProps = useDesktopTopBar();
    useEffect(() => {
        setTopBarProps({
            breadcrumbs: crumb.breadcrumbs,
            title: crumb.title,
            dateFilters: inDrill ? undefined : { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_DESKTOP },
        });
        // `crumb` is memoised on the view, so this re-runs when you drill in or
        // back out - and only then.
    }, [setTopBarProps, crumb, inDrill, datePeriod, setDatePeriod]);
    return (
        <DesktopLayout>
            <main style={{ flex: 1, overflowY: "auto", padding: 24 }}>
                <CustomersBody {...bodyProps} />
            </main>
            {exportSpec && <ExportConsentDialog spec={exportSpec} onClose={closeExport} />}
        </DesktopLayout>
    );
}

/* ─── Mobile shell ────────────────────────────────────────────────────────── */
function MobileView({ crumb, inDrill, datePeriod, setDatePeriod, bodyProps, onRefresh, exportSpec, closeExport }: any) {
    const setTopBarProps = useDesktopTopBar();
    useEffect(() => {
        // Mobile shows the title alone - no room for a crumb trail - so the
        // title carries the drill name and the view's own Back button is the
        // way out.
        setTopBarProps({
            title: crumb.title,
            dateFilters: inDrill ? undefined : { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_MOBILE },
        });
    }, [setTopBarProps, crumb, inDrill, datePeriod, setDatePeriod]);

    const handlePtrRefresh = useCallback(() => { onRefresh(); }, [onRefresh]);
    const { scrollRef, pullDistance, isRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: T.bg }}>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
            <TopBar variant="mobile" />
            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "8px 12px 20px" }}>
                <CustomersBody {...bodyProps} />
                <div style={{ height: 20 }} />
            </div>
            <BottomNav />
            {exportSpec && <ExportConsentDialog spec={exportSpec} onClose={closeExport} />}
        </div>
    );
}

